"use client";

import { useState, useEffect, useMemo } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  FormControl,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Chip,
  Paper,
  Skeleton,
  Stack,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  School,
  AccessTime,
  Public,
  FileDownloadOutlined,
  KeyboardArrowDown,
  Close as CloseIcon,
} from "@mui/icons-material";
import Layout from "../common/Layout";
import { useNotification } from "../../context/NotificationContext";
import apiClient from "../../context/apiClient";

const Analytics = () => {
  const theme = useTheme();
  const { showNotification } = useNotification();
  const [timeRange, setTimeRange] = useState("month");
  const [region, setRegion] = useState("all");
  const [loading, setLoading] = useState(true);

  // --- STATE FOR DIALOG & SEARCH ---
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // --- STATE FOR REGIONAL PERFORMANCE PAGINATION ---
  const [regionalPage, setRegionalPage] = useState(0);
  const [regionalRowsPerPage, setRegionalRowsPerPage] = useState(5);

  const [globalMetrics, setGlobalMetrics] = useState({
    totalTalent: 0,
    activePlacements: 0,
    totalEmployers: 0,
    successRate: 0,
    avgPlacementTime: 0,
  });
  const [regionData, setRegionData] = useState([]);
  const [skillDemand, setSkillDemand] = useState([]);
  const [institutionData, setInstitutionData] = useState([]);

  // --- HANDLERS FOR REGIONAL PAGINATION ---
  const handleRegionalChangePage = (event, newPage) => {
    setRegionalPage(newPage);
  };
  const handleRegionalChangeRowsPerPage = (event) => {
    setRegionalRowsPerPage(parseInt(event.target.value, 10));
    setRegionalPage(0);
  };

  // --- HANDLERS FOR DIALOG ---
  const handleOpenDialog = () => setDialogOpen(true);
  const handleCloseDialog = () => setDialogOpen(false);

  // Memoize the filtered list for the dialog search
  const filteredInstitutions = useMemo(() => {
    if (!searchTerm) {
      return institutionData;
    }
    return institutionData.filter((inst) =>
      inst.institution.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, institutionData]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get("/dashboard/global-overview/");
        const data = response.data;
        setGlobalMetrics({
          totalTalent: data.global_ai_talent || 0,
          activePlacements: data.active_placements || 0,
          totalEmployers: data.global_employer || 0,
          successRate: Number.parseFloat(data.success_rate || 0).toFixed(2),
          avgPlacementTime: Number.parseFloat(
            data.average_days_to_place || 0
          ).toFixed(0),
        });
        const topRolesExamples = [
          ["AI Engineer"],
          ["Java Developer"],
          ["Python", "HTML"],
        ];
        setRegionData(
          (data.placements_by_region || []).map((item, index) => ({
            ...item,
            avgSalary: `$${Number(item.avg_salary || 0).toLocaleString()}`,
            top_roles:
              item.top_roles ||
              topRolesExamples[index % topRolesExamples.length],
          }))
        );
        const skills = data.skills_in_high_demand || [];
        const maxPlacements = Math.max(...skills.map((s) => s.placements), 0);
        setSkillDemand(
          skills.map((item) => ({
            ...item,
            demand:
              maxPlacements > 0 ? (item.placements / maxPlacements) * 100 : 0,
          }))
        );
        setInstitutionData(
          (data.top_performing_institutions || []).map((item) => ({
            institution: item.institution_name || "Unknown Institution",
            graduates: item.graduates || 0,
            placements: item.placements || 0,
            rate: Number.parseFloat(item.success_rate || 0).toFixed(0),
          }))
        );
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
        showNotification(
          "Failed to load dashboard analytics. Please try again later.",
          "error"
        );
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, []); // <-- FIX: Changed dependency array from [showNotification] to []

  const handleExportReport = () => {
    showNotification("Analytics report exported successfully!", "success");
  };

  const kpiCards = [
    { title: "TOTAL AI TALENT", value: globalMetrics.totalTalent },
    { title: "ACTIVE PLACEMENT", value: globalMetrics.activePlacements },
    { title: "GLOBAL EMPLOYERS", value: globalMetrics.totalEmployers },
    { title: "SUCCESS RATE", value: `${globalMetrics.successRate}%` },
    { title: "AVG DAYS TO PLACE", value: globalMetrics.avgPlacementTime },
  ];

  const sectionPaperStyles = {
    p: 3,
    borderRadius: "20px",
    backgroundColor: theme.palette.mint?.main || theme.palette.background.paper,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  };

  return (
    <Layout title="Analytics Dashboard">
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Stack direction="column" spacing={3}>
          {/* ROW 1: HEADER PANEL */}
          <Paper
            elevation={0}
            sx={{
              ...sectionPaperStyles,
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <Box mb={{ xs: 3, md: 0 }}>
              <Typography
                variant="h4"
                sx={{ fontWeight: "bold", color: theme.palette.primary.main }}
              >
                Analytics
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Comprehensive insight into AI Talent export Performance
              </Typography>
            </Box>
            <Box
              display="flex"
              gap={2}
              flexWrap="wrap"
              justifyContent={{ xs: "flex-start", md: "flex-end" }}
            >
              <FormControl sx={{ minWidth: 160 }}>
                <Select
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                  IconComponent={KeyboardArrowDown}
                  size="small"
                  sx={{
                    borderRadius: "50px",
                    color: "primary.main",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "primary.main",
                    },
                    "& .MuiSvgIcon-root": { color: "primary.main" },
                  }}
                  renderValue={(value) => (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <AccessTime fontSize="small" />
                      <Typography variant="body2">
                        {value === "week"
                          ? "Last Week"
                          : value === "month"
                          ? "Last Month"
                          : value === "quarter"
                          ? "Last Quarter"
                          : "Last Year"}
                      </Typography>
                    </Box>
                  )}
                >
                  <MenuItem value="week">Last Week</MenuItem>
                  <MenuItem value="month">Last Month</MenuItem>
                  <MenuItem value="quarter">Last Quarter</MenuItem>
                  <MenuItem value="year">Last Year</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 160 }}>
                <Select
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                  IconComponent={KeyboardArrowDown}
                  size="small"
                  sx={{
                    borderRadius: "50px",
                    color: "primary.main",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "primary.main",
                    },
                    "& .MuiSvgIcon-root": { color: "primary.main" },
                  }}
                  renderValue={(value) => (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Public fontSize="small" />
                      <Typography variant="body2">
                        {value === "all" ? "All Regions" : value}
                      </Typography>
                    </Box>
                  )}
                >
                  <MenuItem value="all">All Regions</MenuItem>
                  <MenuItem value="UAE">UAE</MenuItem>
                  <MenuItem value="USA">USA</MenuItem>
                  <MenuItem value="EU">EU</MenuItem>
                  <MenuItem value="Singapore">Singapore</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<FileDownloadOutlined />}
                onClick={handleExportReport}
                sx={{
                  borderRadius: "50px",
                  textTransform: "none",
                  height: "40px",
                }}
              >
                Export Report
              </Button>
            </Box>
          </Paper>

          {/* ROW 2: KPI CARDS */}
          <Box
            sx={{
              overflowX: { xs: "auto", md: "visible" },
              pb: { xs: 1, md: 0 },
            }}
          >
            <Box display="flex" gap={{ xs: 1.5, md: 2 }}>
              {(loading ? [...Array(5)] : kpiCards).map((item, index) => (
                <Box
                  key={index}
                  sx={{ flex: { md: 1 }, minWidth: { xs: 180, md: 0 } }}
                >
                  {loading ? (
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height={120}
                      sx={{ borderRadius: "14px" }}
                    />
                  ) : (
                    <Card
                      sx={{
                        position: "relative",
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        height: "120px",
                        borderRadius: "14px",
                        overflow: "hidden",
                        boxShadow: theme.shadows[2],
                      }}
                    >
                      <svg
                        width="80"
                        height="120"
                        viewBox="0 0 80 120"
                        style={{ position: "absolute", right: 0, top: 0 }}
                      >
                        <path
                          d="M20 0 Q60 30 40 60 Q20 90 60 120 L80 120 L80 0 Z"
                          fill={alpha(theme.palette.primary.light, 0.3)}
                        />
                      </svg>
                      <CardContent
                        sx={{
                          position: "relative",
                          zIndex: 1,
                          p: { xs: 1.5, md: 2 },
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: "bold",
                            fontSize: { xs: "0.7rem", md: "0.8rem" },
                            textTransform: "uppercase",
                            color: alpha(
                              theme.palette.primary.contrastText,
                              0.8
                            ),
                            mb: 1,
                            lineHeight: 1.2,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: "bold",
                            fontSize: { xs: "1.5rem", md: "1.75rem" },
                            lineHeight: 1,
                          }}
                        >
                          {item.value}
                        </Typography>
                      </CardContent>
                    </Card>
                  )}
                </Box>
              ))}
            </Box>
          </Box>

          {/* ROW 3: TWO COLUMNS (SKILLS & INSTITUTIONS) */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 3,
            }}
          >
            <Box sx={{ flex: { md: 1 }, minWidth: 0 }}>
              <Paper
                elevation={0}
                sx={{
                  ...sectionPaperStyles,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    mb: 2,
                    color: theme.palette.primary.main,
                  }}
                >
                  Skill In High Demand
                </Typography>
                <Box sx={{ maxHeight: 450, overflowY: "auto", pr: 1 }}>
                  <Stack spacing={1.5}>
                    {(loading ? [...Array(5)] : skillDemand).map(
                      (skill, index) => (
                        <Paper
                          key={index}
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: "12px",
                            border: `1px solid ${theme.palette.divider}`,
                            backgroundColor: "background.paper",
                          }}
                        >
                          {loading ? (
                            <Skeleton height={60} />
                          ) : (
                            <>
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                mb={1}
                              >
                                <Typography
                                  variant="body1"
                                  sx={{ fontWeight: "bold" }}
                                >
                                  {skill.skill}
                                </Typography>
                                <Chip
                                  label={`+${skill.growth}%`}
                                  size="small"
                                  sx={{
                                    backgroundColor: alpha(
                                      theme.palette.warning.main,
                                      0.2
                                    ),
                                    color:
                                      theme.palette.warning.dark ||
                                      theme.palette.warning.main,
                                    fontWeight: "bold",
                                  }}
                                />
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={skill.demand}
                                color="success"
                                sx={{
                                  height: 6,
                                  borderRadius: 3,
                                  mb: 1,
                                  backgroundColor: "divider",
                                }}
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {skill.placements.toLocaleString()} Placement
                              </Typography>
                            </>
                          )}
                        </Paper>
                      )
                    )}
                  </Stack>
                </Box>
              </Paper>
            </Box>
            <Box sx={{ flex: { md: 2 }, minWidth: 0 }}>
              <Paper
                elevation={0}
                sx={{
                  ...sectionPaperStyles,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    mb: 2,
                    color: theme.palette.primary.main,
                  }}
                >
                  Top 5 Performing Institutions
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      {(loading
                        ? [...Array(5)]
                        : institutionData.slice(0, 5)
                      ).map((inst, index) => (
                        <TableRow
                          key={index}
                          sx={{ "& td, & th": { border: 0, py: 1.5 } }}
                        >
                          {loading ? (
                            <TableCell colSpan={5}>
                              <Skeleton />
                            </TableCell>
                          ) : (
                            <>
                              <TableCell
                                sx={{ width: "1%", fontWeight: "bold" }}
                              >
                                {index + 1}.
                              </TableCell>
                              <TableCell sx={{ width: "49%" }}>
                                <Box display="flex" alignItems="center">
                                  <School
                                    sx={{
                                      mr: 1.5,
                                      fontSize: 18,
                                      color: "text.secondary",
                                    }}
                                  />
                                  <Typography variant="body2" fontWeight="bold">
                                    {inst.institution}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                {inst.graduates}{" "}
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Grads
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                {inst.placements}{" "}
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Hired
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={`${inst.rate}%`}
                                  size="small"
                                  sx={{
                                    backgroundColor:
                                      inst.rate >= 90
                                        ? alpha(
                                            theme.palette.success.main,
                                            0.15
                                          )
                                        : alpha(
                                            theme.palette.warning.main,
                                            0.15
                                          ),
                                    color:
                                      inst.rate >= 90
                                        ? theme.palette.success.dark
                                        : theme.palette.warning.dark,
                                    fontWeight: "bold",
                                  }}
                                />
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box
                  sx={{
                    mt: "auto",
                    pt: 1,
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Button size="small" onClick={handleOpenDialog}>
                    See All
                  </Button>
                </Box>
              </Paper>
            </Box>
          </Box>

          {/* ROW 4: REGIONAL PERFORMANCE */}
          <Paper elevation={0} sx={sectionPaperStyles}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                mb: 2,
                color: theme.palette.primary.main,
              }}
            >
              Regional Performance
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Region</TableCell>
                    <TableCell>Placement</TableCell>
                    <TableCell>Growth</TableCell>
                    <TableCell>Avg Salary</TableCell>
                    <TableCell>Demand Score</TableCell>
                    <TableCell>Top Roles</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(loading
                    ? [...Array(regionalRowsPerPage)]
                    : regionData.slice(
                        regionalPage * regionalRowsPerPage,
                        regionalPage * regionalRowsPerPage + regionalRowsPerPage
                      )
                  ).map((item, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      {loading ? (
                        <TableCell colSpan={6}>
                          <Skeleton />
                        </TableCell>
                      ) : (
                        <>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            {item.region}
                          </TableCell>
                          <TableCell
                            sx={{ color: "error.main", fontWeight: 500 }}
                          >
                            {item.placements.toLocaleString()}
                          </TableCell>
                          <TableCell
                            sx={{ color: "success.main", fontWeight: 500 }}
                          >
                            +{item.growth}%
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            {item.avgSalary}
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <LinearProgress
                                variant="determinate"
                                value={item.demand_score}
                                color="success"
                                sx={{
                                  width: 60,
                                  mr: 1,
                                  backgroundColor: "divider",
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{ color: "success.main", fontWeight: 500 }}
                              >
                                {item.demand_score}%
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {(item.top_roles || []).join(", ")}
                            </Typography>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={regionData.length}
              page={regionalPage}
              onPageChange={handleRegionalChangePage}
              rowsPerPage={regionalRowsPerPage}
              onRowsPerPageChange={handleRegionalChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
              sx={{ pt: 1 }}
            />
          </Paper>
        </Stack>
      </Box>

      {/* DIALOG FOR ALL INSTITUTIONS */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          All Performing Institutions
          <IconButton onClick={handleCloseDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            label="Search by Institution Name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TableContainer component={Paper} elevation={0}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "5%" }}>Sr.No.</TableCell>
                  <TableCell>Institution</TableCell>
                  <TableCell align="center">Graduates</TableCell>
                  <TableCell align="center">Placements</TableCell>
                  <TableCell align="right">Success Rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredInstitutions.map((inst, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      {index + 1}.
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <School
                          sx={{
                            mr: 1.5,
                            fontSize: 18,
                            color: "text.secondary",
                          }}
                        />
                        <Typography variant="body2">
                          {inst.institution}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">{inst.graduates}</TableCell>
                    <TableCell align="center">{inst.placements}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${inst.rate}%`}
                        size="small"
                        sx={{
                          backgroundColor:
                            inst.rate >= 90
                              ? alpha(theme.palette.success.main, 0.15)
                              : alpha(theme.palette.warning.main, 0.15),
                          color:
                            inst.rate >= 90
                              ? theme.palette.success.dark
                              : theme.palette.warning.dark,
                          fontWeight: "bold",
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default Analytics;


// // // // // // "use client";

// // // // // // import { useState, useEffect } from "react";
// // // // // // import {
// // // // // //   Grid,
// // // // // //   Card,
// // // // // //   CardContent,
// // // // // //   Typography,
// // // // // //   Box,
// // // // // //   FormControl,
// // // // // //   Select,
// // // // // //   MenuItem,
// // // // // //   Button,
// // // // // //   Table,
// // // // // //   TableBody,
// // // // // //   TableCell,
// // // // // //   TableContainer,
// // // // // //   TableHead,
// // // // // //   TableRow,
// // // // // //   LinearProgress,
// // // // // //   Chip,
// // // // // //   Paper,
// // // // // //   Skeleton,
// // // // // // } from "@mui/material";
// // // // // // import {
// // // // // //   School,
// // // // // //   AccessTime,
// // // // // //   Public,
// // // // // //   FileDownloadOutlined,
// // // // // //   KeyboardArrowDown,
// // // // // // } from "@mui/icons-material";
// // // // // // import Layout from "../common/Layout";
// // // // // // import { useNotification } from "../../context/NotificationContext";
// // // // // // import apiClient from "../../context/apiClient";

// // // // // // const Analytics = () => {
// // // // // //   const { showNotification } = useNotification();
// // // // // //   const [timeRange, setTimeRange] = useState("month");
// // // // // //   const [region, setRegion] = useState("all");
// // // // // //   const [loading, setLoading] = useState(true);

// // // // // //   const [globalMetrics, setGlobalMetrics] = useState({
// // // // // //     totalTalent: 0,
// // // // // //     activePlacements: 0,
// // // // // //     totalEmployers: 0,
// // // // // //     successRate: 0,
// // // // // //     avgPlacementTime: 0,
// // // // // //   });
// // // // // //   const [regionData, setRegionData] = useState([]);
// // // // // //   const [skillDemand, setSkillDemand] = useState([]);
// // // // // //   const [institutionData, setInstitutionData] = useState([]);

// // // // // //   useEffect(() => {
// // // // // //     const fetchDashboardData = async () => {
// // // // // //       setLoading(true);
// // // // // //       try {
// // // // // //         const response = await apiClient.get("/dashboard/global-overview/");
// // // // // //         const data = response.data;

// // // // // //         setGlobalMetrics({
// // // // // //           totalTalent: data.global_ai_talent || 0,
// // // // // //           activePlacements: data.active_placements || 0,
// // // // // //           totalEmployers: data.global_employer || 0,
// // // // // //           successRate: Number.parseFloat(data.success_rate || 0).toFixed(2),
// // // // // //           avgPlacementTime: Number.parseFloat(
// // // // // //             data.average_days_to_place || 0
// // // // // //           ).toFixed(0),
// // // // // //         });

// // // // // //         const topRolesExamples = [
// // // // // //           ["AI Engineer"],
// // // // // //           ["Java Developer"],
// // // // // //           ["Python", "HTML"],
// // // // // //           ["UIUX Designer"],
// // // // // //           ["Graphic Designer"],
// // // // // //           ["IT Support"],
// // // // // //           ["Visual Designer"],
// // // // // //         ];

// // // // // //         setRegionData(
// // // // // //           (data.placements_by_region || []).map((item, index) => ({
// // // // // //             ...item,
// // // // // //             avgSalary: `$${Number(item.avg_salary || 0).toLocaleString()}`,
// // // // // //             top_roles:
// // // // // //               item.top_roles ||
// // // // // //               topRolesExamples[index % topRolesExamples.length],
// // // // // //           }))
// // // // // //         );

// // // // // //         const skills = data.skills_in_high_demand || [];
// // // // // //         const maxPlacements = Math.max(...skills.map((s) => s.placements), 0);
// // // // // //         setSkillDemand(
// // // // // //           skills.map((item) => ({
// // // // // //             ...item,
// // // // // //             demand:
// // // // // //               maxPlacements > 0 ? (item.placements / maxPlacements) * 100 : 0,
// // // // // //           }))
// // // // // //         );

// // // // // //         setInstitutionData(
// // // // // //           (data.top_performing_institutions || []).map((item) => ({
// // // // // //             institution: item.institution_name || "Unknown Institution",
// // // // // //             graduates: item.graduates || 0,
// // // // // //             placements: item.placements || 0,
// // // // // //             rate: Number.parseFloat(item.success_rate || 0).toFixed(0),
// // // // // //           }))
// // // // // //         );
// // // // // //       } catch (error) {
// // // // // //         console.error("Failed to fetch dashboard data:", error);
// // // // // //         showNotification(
// // // // // //           "Failed to load dashboard analytics. Please try again later.",
// // // // // //           "error"
// // // // // //         );
// // // // // //       } finally {
// // // // // //         setLoading(false);
// // // // // //       }
// // // // // //     };

// // // // // //     fetchDashboardData();
// // // // // //   }, [showNotification]);

// // // // // //   const handleExportReport = () => {
// // // // // //     showNotification("Analytics report exported successfully!", "success");
// // // // // //   };

// // // // // //   const kpiCards = [
// // // // // //     { title: "TOTAL AI TALENT", value: globalMetrics.totalTalent, growth: "" },
// // // // // //     {
// // // // // //       title: "ACTIVE PLACEMENT",
// // // // // //       value: globalMetrics.activePlacements,
// // // // // //       growth: "",
// // // // // //     },
// // // // // //     {
// // // // // //       title: "GLOBAL EMPLOYEERS",
// // // // // //       value: globalMetrics.totalEmployers,
// // // // // //       growth: "",
// // // // // //     },
// // // // // //     { title: "SUCCESS RATE", value: globalMetrics.successRate, growth: "" },
// // // // // //     {
// // // // // //       title: "AVG DAYS TO PLACE",
// // // // // //       value: globalMetrics.avgPlacementTime,
// // // // // //       growth: "",
// // // // // //     },
// // // // // //   ];

// // // // // //   const SkeletonKPICards = () => (
// // // // // //     <Box display="flex" gap="18px" mb={3} sx={{ overflowX: "auto" }}>
// // // // // //       {[...Array(5)].map((_, index) => (
// // // // // //         <Card
// // // // // //           key={index}
// // // // // //           sx={{
// // // // // //             position: "relative",
// // // // // //             backgroundColor: "#5A36CC",
// // // // // //             minWidth: "200px",
// // // // // //             width: "200px",
// // // // // //             height: "120px",
// // // // // //             borderRadius: "14px",
// // // // // //             overflow: "hidden",
// // // // // //             boxShadow: "0 4px 12px rgba(90, 54, 204, 0.25)",
// // // // // //             flexShrink: 0,
// // // // // //           }}
// // // // // //         >
// // // // // //           <svg
// // // // // //             width="80"
// // // // // //             height="120"
// // // // // //             viewBox="0 0 80 120"
// // // // // //             style={{
// // // // // //               position: "absolute",
// // // // // //               right: 0,
// // // // // //               top: 0,
// // // // // //             }}
// // // // // //           >
// // // // // //             <path
// // // // // //               d="M20 0 Q60 30 40 60 Q20 90 60 120 L80 120 L80 0 Z"
// // // // // //               fill="#F9B64C"
// // // // // //             />
// // // // // //           </svg>
// // // // // //           <CardContent sx={{ position: "relative", zIndex: 1, p: "16px" }}>
// // // // // //             <Skeleton
// // // // // //               variant="text"
// // // // // //               width="80%"
// // // // // //               height={16}
// // // // // //               sx={{ bgcolor: "rgba(195,195,195,0.3)", mb: 1 }}
// // // // // //             />
// // // // // //             <Skeleton
// // // // // //               variant="text"
// // // // // //               width="60%"
// // // // // //               height={32}
// // // // // //               sx={{ bgcolor: "rgba(255,255,255,0.3)", mb: 1 }}
// // // // // //             />
// // // // // //             <Skeleton
// // // // // //               variant="text"
// // // // // //               width="70%"
// // // // // //               height={14}
// // // // // //               sx={{ bgcolor: "rgba(255,255,255,0.3)" }}
// // // // // //             />
// // // // // //           </CardContent>
// // // // // //         </Card>
// // // // // //       ))}
// // // // // //     </Box>
// // // // // //   );

// // // // // //   const SkeletonRegionalPerformance = () => (
// // // // // //     <Box
// // // // // //       sx={{
// // // // // //         backgroundColor: "#F1FFFA",
// // // // // //         p: 3,
// // // // // //         borderRadius: "20px",
// // // // // //         boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
// // // // // //         mb: 3,
// // // // // //       }}
// // // // // //     >
// // // // // //       <Skeleton variant="text" width={200} height={32} sx={{ mb: 2 }} />
// // // // // //       <TableContainer
// // // // // //         component={Paper}
// // // // // //         elevation={0}
// // // // // //         sx={{ backgroundColor: "transparent" }}
// // // // // //       >
// // // // // //         <Table>
// // // // // //           <TableHead>
// // // // // //             <TableRow>
// // // // // //               {[
// // // // // //                 "Region",
// // // // // //                 "Placement",
// // // // // //                 "Growth",
// // // // // //                 "Avg Salary",
// // // // // //                 "Demand Score",
// // // // // //                 "Top Roles",
// // // // // //               ].map((header) => (
// // // // // //                 <TableCell key={header} sx={{ border: 0 }}>
// // // // // //                   <Skeleton variant="text" width={80} height={20} />
// // // // // //                 </TableCell>
// // // // // //               ))}
// // // // // //             </TableRow>
// // // // // //           </TableHead>
// // // // // //           <TableBody>
// // // // // //             {[...Array(3)].map((_, index) => (
// // // // // //               <TableRow
// // // // // //                 key={index}
// // // // // //                 sx={{
// // // // // //                   backgroundColor: "white",
// // // // // //                   "& > *": { border: 0 },
// // // // // //                   borderRadius: "12px",
// // // // // //                 }}
// // // // // //               >
// // // // // //                 <TableCell>
// // // // // //                   <Skeleton variant="text" width={60} height={20} />
// // // // // //                 </TableCell>
// // // // // //                 <TableCell>
// // // // // //                   <Skeleton variant="text" width={40} height={20} />
// // // // // //                 </TableCell>
// // // // // //                 <TableCell>
// // // // // //                   <Skeleton variant="text" width={50} height={20} />
// // // // // //                 </TableCell>
// // // // // //                 <TableCell>
// // // // // //                   <Skeleton variant="text" width={80} height={20} />
// // // // // //                 </TableCell>
// // // // // //                 <TableCell>
// // // // // //                   <Box display="flex" alignItems="center">
// // // // // //                     <Skeleton
// // // // // //                       variant="rectangular"
// // // // // //                       width={60}
// // // // // //                       height={4}
// // // // // //                       sx={{ mr: 1, borderRadius: 2 }}
// // // // // //                     />
// // // // // //                     <Skeleton variant="text" width={40} height={20} />
// // // // // //                   </Box>
// // // // // //                 </TableCell>
// // // // // //                 <TableCell>
// // // // // //                   <Skeleton variant="text" width={120} height={20} />
// // // // // //                 </TableCell>
// // // // // //               </TableRow>
// // // // // //             ))}
// // // // // //           </TableBody>
// // // // // //         </Table>
// // // // // //       </TableContainer>
// // // // // //     </Box>
// // // // // //   );

// // // // // //   const SkeletonInstitutions = () => (
// // // // // //     <Box
// // // // // //       sx={{
// // // // // //         backgroundColor: "#F1FFFA",
// // // // // //         p: 3,
// // // // // //         borderRadius: "20px",
// // // // // //         boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
// // // // // //       }}
// // // // // //     >
// // // // // //       <Skeleton variant="text" width={250} height={32} sx={{ mb: 2 }} />
// // // // // //       <TableContainer
// // // // // //         component={Paper}
// // // // // //         elevation={0}
// // // // // //         sx={{ backgroundColor: "transparent" }}
// // // // // //       >
// // // // // //         <Table size="small">
// // // // // //           <TableHead>
// // // // // //             <TableRow>
// // // // // //               {["INSTITUTION", "GRADUATES", "PLACEMENTS", "SUCCESS RATE"].map(
// // // // // //                 (header) => (
// // // // // //                   <TableCell key={header} sx={{ border: 0 }}>
// // // // // //                     <Skeleton variant="text" width={80} height={20} />
// // // // // //                   </TableCell>
// // // // // //                 )
// // // // // //               )}
// // // // // //             </TableRow>
// // // // // //           </TableHead>
// // // // // //           <TableBody>
// // // // // //             {[...Array(2)].map((_, index) => (
// // // // // //               <TableRow
// // // // // //                 key={index}
// // // // // //                 sx={{ backgroundColor: "white", "& > *": { border: 0 } }}
// // // // // //               >
// // // // // //                 <TableCell>
// // // // // //                   <Box display="flex" alignItems="center">
// // // // // //                     <Skeleton
// // // // // //                       variant="circular"
// // // // // //                       width={16}
// // // // // //                       height={16}
// // // // // //                       sx={{ mr: 1 }}
// // // // // //                     />
// // // // // //                     <Skeleton variant="text" width={180} height={20} />
// // // // // //                   </Box>
// // // // // //                 </TableCell>
// // // // // //                 <TableCell align="right">
// // // // // //                   <Skeleton variant="text" width={30} height={20} />
// // // // // //                 </TableCell>
// // // // // //                 <TableCell align="right">
// // // // // //                   <Skeleton variant="text" width={30} height={20} />
// // // // // //                 </TableCell>
// // // // // //                 <TableCell align="right">
// // // // // //                   <Skeleton
// // // // // //                     variant="rectangular"
// // // // // //                     width={50}
// // // // // //                     height={24}
// // // // // //                     sx={{ borderRadius: 12 }}
// // // // // //                   />
// // // // // //                 </TableCell>
// // // // // //               </TableRow>
// // // // // //             ))}
// // // // // //           </TableBody>
// // // // // //         </Table>
// // // // // //       </TableContainer>
// // // // // //     </Box>
// // // // // //   );

// // // // // //   const SkeletonSkillDemand = () => (
// // // // // //     <Box
// // // // // //       sx={{
// // // // // //         backgroundColor: "#F1FFFA",
// // // // // //         p: 3,
// // // // // //         borderRadius: "20px",
// // // // // //         boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
// // // // // //         height: "fit-content",
// // // // // //       }}
// // // // // //     >
// // // // // //       <Skeleton variant="text" width={180} height={32} sx={{ mb: 2 }} />
// // // // // //       {[...Array(4)].map((_, index) => (
// // // // // //         <Paper
// // // // // //           key={index}
// // // // // //           elevation={0}
// // // // // //           sx={{
// // // // // //             p: 2,
// // // // // //             mb: 1.5,
// // // // // //             borderRadius: "12px",
// // // // // //             border: "1px solid #EAF2F0",
// // // // // //             backgroundColor: "white",
// // // // // //           }}
// // // // // //         >
// // // // // //           <Box
// // // // // //             display="flex"
// // // // // //             justifyContent="space-between"
// // // // // //             alignItems="center"
// // // // // //             mb={1}
// // // // // //           >
// // // // // //             <Skeleton variant="text" width={100} height={24} />
// // // // // //             <Skeleton
// // // // // //               variant="rectangular"
// // // // // //               width={60}
// // // // // //               height={24}
// // // // // //               sx={{ borderRadius: 12 }}
// // // // // //             />
// // // // // //           </Box>
// // // // // //           <Skeleton
// // // // // //             variant="rectangular"
// // // // // //             width="100%"
// // // // // //             height={6}
// // // // // //             sx={{ borderRadius: 3, mb: 1 }}
// // // // // //           />
// // // // // //           <Skeleton variant="text" width={80} height={16} />
// // // // // //         </Paper>
// // // // // //       ))}
// // // // // //     </Box>
// // // // // //   );

// // // // // //   if (loading) {
// // // // // //     return (
// // // // // //       <Layout title="Analytics Dashboard">
// // // // // //         <Box sx={{ p: 3, backgroundColor: "white" }}>
// // // // // //           <Box
// // // // // //             display="flex"
// // // // // //             justifyContent="space-between"
// // // // // //             alignItems={{ xs: "flex-start", md: "center" }}
// // // // // //             flexDirection={{ xs: "column", md: "row" }}
// // // // // //             mb={4}
// // // // // //             backgroundColor="#F1FFFA"
// // // // // //             sx={{
// // // // // //               p: 3,
// // // // // //               borderRadius: "20px",
// // // // // //               boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
// // // // // //             }}
// // // // // //           >
// // // // // //             <Box mb={{ xs: 3, md: 0 }}>
// // // // // //               <Skeleton variant="text" width={300} height={40} />
// // // // // //               <Skeleton variant="text" width={400} height={20} />
// // // // // //             </Box>
// // // // // //             <Box
// // // // // //               display="flex"
// // // // // //               gap={2}
// // // // // //               flexWrap="wrap"
// // // // // //               justifyContent={{ xs: "flex-start", md: "flex-end" }}
// // // // // //             >
// // // // // //               <Skeleton
// // // // // //                 variant="rectangular"
// // // // // //                 width={120}
// // // // // //                 height={40}
// // // // // //                 sx={{ borderRadius: "50px" }}
// // // // // //               />
// // // // // //               <Skeleton
// // // // // //                 variant="rectangular"
// // // // // //                 width={120}
// // // // // //                 height={40}
// // // // // //                 sx={{ borderRadius: "50px" }}
// // // // // //               />
// // // // // //               <Skeleton
// // // // // //                 variant="rectangular"
// // // // // //                 width={140}
// // // // // //                 height={40}
// // // // // //                 sx={{ borderRadius: "50px" }}
// // // // // //               />
// // // // // //             </Box>
// // // // // //           </Box>

// // // // // //           <SkeletonKPICards />

// // // // // //           <Grid container spacing={3} mb={3}>
// // // // // //             <Grid item xs={12} lg={6}>
// // // // // //               <SkeletonRegionalPerformance />
// // // // // //               <SkeletonInstitutions />
// // // // // //             </Grid>
// // // // // //             <Grid item xs={12} lg={6}>
// // // // // //               <SkeletonSkillDemand />
// // // // // //             </Grid>
// // // // // //           </Grid>
// // // // // //         </Box>
// // // // // //       </Layout>
// // // // // //     );
// // // // // //   }

// // // // // //   return (
// // // // // //     <Layout title="Analytics Dashboard">
// // // // // //       <Box sx={{ p: 3, backgroundColor: "white" }}>
// // // // // //         <Box
// // // // // //           display="flex"
// // // // // //           justifyContent="space-between"
// // // // // //           alignItems={{ xs: "flex-start", md: "center" }}
// // // // // //           flexDirection={{ xs: "column", md: "row" }}
// // // // // //           mb={4}
// // // // // //           sx={{
// // // // // //             p: 3,
// // // // // //             borderRadius: "20px",
// // // // // //             boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
// // // // // //             backgroundColor: "#F1FFFA",
// // // // // //           }}
// // // // // //         >
// // // // // //           <Box mb={{ xs: 3, md: 0 }}>
// // // // // //             <Typography
// // // // // //               variant="h4"
// // // // // //               sx={{ fontWeight: "bold", color: "#4F46BA" }}
// // // // // //             >
// // // // // //               Analytics
// // // // // //             </Typography>
// // // // // //             <Typography variant="body1" color="#A9A5A5">
// // // // // //               Comprehensive insight into AI Talent export Performance
// // // // // //             </Typography>
// // // // // //           </Box>
// // // // // //           <Box
// // // // // //             display="flex"
// // // // // //             gap={2}
// // // // // //             flexWrap="wrap"
// // // // // //             justifyContent={{ xs: "flex-start", md: "flex-end" }}
// // // // // //           >
// // // // // //             <FormControl sx={{ minWidth: 160 }}>
// // // // // //               <Select
// // // // // //                 value={timeRange}
// // // // // //                 onChange={(e) => setTimeRange(e.target.value)}
// // // // // //                 IconComponent={KeyboardArrowDown}
// // // // // //                 sx={{
// // // // // //                   borderRadius: "50px",
// // // // // //                   height: "40px",
// // // // // //                   borderColor: "#5A36CC",
// // // // // //                   color: "#5A36CC",
// // // // // //                   ".MuiSelect-select": {
// // // // // //                     display: "flex",
// // // // // //                     alignItems: "center",
// // // // // //                     gap: 1,
// // // // // //                     py: 0,
// // // // // //                   },
// // // // // //                   "& .MuiOutlinedInput-notchedOutline": {
// // // // // //                     borderColor: "rgba(0, 0, 0, 0.23)",
// // // // // //                   },
// // // // // //                 }}
// // // // // //                 renderValue={(value) => (
// // // // // //                   <Box
// // // // // //                     sx={{
// // // // // //                       display: "flex",
// // // // // //                       alignItems: "center",
// // // // // //                       gap: 1,
// // // // // //                       borderColor: "#5A36CC",
// // // // // //                     }}
// // // // // //                   >
// // // // // //                     <AccessTime
// // // // // //                       sx={{ color: "#5A36CC", borderColor: "#5A36CC" }}
// // // // // //                     />
// // // // // //                     <Typography>
// // // // // //                       {value === "week"
// // // // // //                         ? "Last Week"
// // // // // //                         : value === "month"
// // // // // //                         ? "Last Month"
// // // // // //                         : value === "quarter"
// // // // // //                         ? "Last Quarter"
// // // // // //                         : "Last Year"}
// // // // // //                     </Typography>
// // // // // //                   </Box>
// // // // // //                 )}
// // // // // //               >
// // // // // //                 <MenuItem value="week">Last Week</MenuItem>
// // // // // //                 <MenuItem value="month">Last Month</MenuItem>
// // // // // //                 <MenuItem value="quarter">Last Quarter</MenuItem>
// // // // // //                 <MenuItem value="year">Last Year</MenuItem>
// // // // // //               </Select>
// // // // // //             </FormControl>
// // // // // //             <FormControl sx={{ minWidth: 160 }}>
// // // // // //               <Select
// // // // // //                 value={region}
// // // // // //                 onChange={(e) => setRegion(e.target.value)}
// // // // // //                 IconComponent={KeyboardArrowDown}
// // // // // //                 sx={{
// // // // // //                   borderRadius: "50px",
// // // // // //                   borderColor: "#5A36CC",
// // // // // //                   height: "40px",
// // // // // //                   ".MuiSelect-select": {
// // // // // //                     display: "flex",
// // // // // //                     alignItems: "center",
// // // // // //                     gap: 1,
// // // // // //                     py: 0,
// // // // // //                   },
// // // // // //                 }}
// // // // // //                 renderValue={(value) => (
// // // // // //                   <Box
// // // // // //                     sx={{
// // // // // //                       display: "flex",
// // // // // //                       alignItems: "center",
// // // // // //                       gap: 1,
// // // // // //                       color: "#5A36CC",
// // // // // //                       borderColor: "#5A36CC",
// // // // // //                     }}
// // // // // //                   >
// // // // // //                     <Public sx={{ color: "#5A36CC", borderColor: "#5A36CC" }} />
// // // // // //                     <Typography>
// // // // // //                       {value === "all" ? "All Regions" : value}
// // // // // //                     </Typography>
// // // // // //                   </Box>
// // // // // //                 )}
// // // // // //               >
// // // // // //                 <MenuItem value="all">All Regions</MenuItem>
// // // // // //                 <MenuItem value="UAE">UAE</MenuItem>
// // // // // //                 <MenuItem value="USA">USA</MenuItem>
// // // // // //                 <MenuItem value="EU">EU</MenuItem>
// // // // // //                 <MenuItem value="Singapore">Singapore</MenuItem>
// // // // // //               </Select>
// // // // // //             </FormControl>
// // // // // //             <Button
// // // // // //               variant="outlined"
// // // // // //               startIcon={<FileDownloadOutlined />}
// // // // // //               onClick={handleExportReport}
// // // // // //               sx={{
// // // // // //                 borderRadius: "50px",
// // // // // //                 textTransform: "none",
// // // // // //                 height: "40px",
// // // // // //                 borderColor: "#5A36CC",
// // // // // //                 color: "#5A36CC",
// // // // // //               }}
// // // // // //             >
// // // // // //               Export Report
// // // // // //             </Button>
// // // // // //           </Box>
// // // // // //         </Box>

// // // // // //         <Box display="flex" gap="18px" mb={3} sx={{ overflowX: "auto" }}>
// // // // // //           {kpiCards.map((item, index) => (
// // // // // //             <Card
// // // // // //               key={index}
// // // // // //               sx={{
// // // // // //                 position: "relative",
// // // // // //                 backgroundColor: "#5A36CC",
// // // // // //                 minWidth: "200px",
// // // // // //                 width: "200px",
// // // // // //                 height: "120px",
// // // // // //                 borderRadius: "14px",
// // // // // //                 overflow: "hidden",
// // // // // //                 boxShadow: "0 4px 12px rgba(90, 54, 204, 0.25)",
// // // // // //                 flexShrink: 0,
// // // // // //               }}
// // // // // //             >
// // // // // //               <svg
// // // // // //                 width="80"
// // // // // //                 height="120"
// // // // // //                 viewBox="0 0 80 120"
// // // // // //                 style={{
// // // // // //                   position: "absolute",
// // // // // //                   right: 0,
// // // // // //                   top: 0,
// // // // // //                 }}
// // // // // //               >
// // // // // //                 <path
// // // // // //                   d="M20 0 Q60 30 40 60 Q20 90 60 120 L80 120 L80 0 Z"
// // // // // //                   fill="#F9B64C"
// // // // // //                 />
// // // // // //               </svg>
// // // // // //               <CardContent sx={{ position: "relative", zIndex: 1, p: "16px" }}>
// // // // // //                 <Typography
// // // // // //                   sx={{
// // // // // //                     fontWeight: "bold",
// // // // // //                     fontSize: "14px",
// // // // // //                     textTransform: "uppercase",
// // // // // //                     color: "#C3C3C3",
// // // // // //                     mb: "8px",
// // // // // //                     lineHeight: 1.2,
// // // // // //                   }}
// // // // // //                 >
// // // // // //                   {item.title}
// // // // // //                 </Typography>
// // // // // //                 <Typography
// // // // // //                   sx={{
// // // // // //                     fontWeight: "bold",
// // // // // //                     fontSize: "26px",
// // // // // //                     color: "#FFFFFF",
// // // // // //                     mb: "4px",
// // // // // //                     lineHeight: 1,
// // // // // //                   }}
// // // // // //                 >
// // // // // //                   {item.value}
// // // // // //                 </Typography>
// // // // // //                 <Typography
// // // // // //                   sx={{
// // // // // //                     fontSize: "12px",
// // // // // //                     color: "#FFFFFF",
// // // // // //                     lineHeight: 1,
// // // // // //                   }}
// // // // // //                 >
// // // // // //                   {item.growth}
// // // // // //                 </Typography>
// // // // // //               </CardContent>
// // // // // //             </Card>
// // // // // //           ))}
// // // // // //         </Box>

// // // // // //         <Grid container spacing={3} mb={3}>
// // // // // //           <Grid item xs={12} lg={6}>
// // // // // //             <Box
// // // // // //               sx={{
// // // // // //                 backgroundColor: "#F1FFFA",
// // // // // //                 p: 3,
// // // // // //                 borderRadius: "20px",
// // // // // //                 boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
// // // // // //                 mb: 3,
// // // // // //               }}
// // // // // //             >
// // // // // //               <Typography
// // // // // //                 variant="h6"
// // // // // //                 sx={{ fontWeight: "bold", mb: 2, color: "#5A36CC" }}
// // // // // //               >
// // // // // //                 Regional Performance
// // // // // //               </Typography>
// // // // // //               <TableContainer
// // // // // //                 component={Paper}
// // // // // //                 elevation={0}
// // // // // //                 sx={{ backgroundColor: "transparent" }}
// // // // // //               >
// // // // // //                 <Table>
// // // // // //                   <TableHead>
// // // // // //                     <TableRow>
// // // // // //                       <TableCell sx={{ border: 0, color: "#A9A5A5" }}>
// // // // // //                         Region
// // // // // //                       </TableCell>
// // // // // //                       <TableCell sx={{ border: 0, color: "#A9A5A5" }}>
// // // // // //                         Placement
// // // // // //                       </TableCell>
// // // // // //                       <TableCell sx={{ border: 0, color: "#A9A5A5" }}>
// // // // // //                         Growth
// // // // // //                       </TableCell>
// // // // // //                       <TableCell sx={{ border: 0, color: "#A9A5A5" }}>
// // // // // //                         Avg Salary
// // // // // //                       </TableCell>
// // // // // //                       <TableCell sx={{ border: 0, color: "#A9A5A5" }}>
// // // // // //                         Demand Score
// // // // // //                       </TableCell>
// // // // // //                       <TableCell sx={{ border: 0, color: "#A9A5A5" }}>
// // // // // //                         Top Roles
// // // // // //                       </TableCell>
// // // // // //                     </TableRow>
// // // // // //                   </TableHead>
// // // // // //                   <TableBody>
// // // // // //                     {regionData.map((item, index) => (
// // // // // //                       <TableRow
// // // // // //                         key={`${item.region}-${index}`}
// // // // // //                         sx={{
// // // // // //                           backgroundColor: "white",
// // // // // //                           "& > *": { border: 0 },
// // // // // //                           borderRadius: "12px",
// // // // // //                         }}
// // // // // //                       >
// // // // // //                         <TableCell sx={{ fontWeight: "bold" }}>
// // // // // //                           {item.region}
// // // // // //                         </TableCell>
// // // // // //                         <TableCell sx={{ color: "#D32F2F", fontWeight: 500 }}>
// // // // // //                           {item.placements.toLocaleString()}
// // // // // //                         </TableCell>
// // // // // //                         <TableCell sx={{ color: "#D32F2F", fontWeight: 500 }}>
// // // // // //                           +{item.growth}%
// // // // // //                         </TableCell>
// // // // // //                         <TableCell sx={{ fontWeight: "bold" }}>
// // // // // //                           {item.avgSalary}
// // // // // //                         </TableCell>
// // // // // //                         <TableCell>
// // // // // //                           <Box display="flex" alignItems="center">
// // // // // //                             <LinearProgress
// // // // // //                               variant="determinate"
// // // // // //                               value={item.demand_score}
// // // // // //                               sx={{
// // // // // //                                 width: 60,
// // // // // //                                 height: 4,
// // // // // //                                 borderRadius: 2,
// // // // // //                                 mr: 1,
// // // // // //                                 backgroundColor: "#E0E0E0",
// // // // // //                               }}
// // // // // //                             />
// // // // // //                             <Typography
// // // // // //                               variant="caption"
// // // // // //                               sx={{ color: "#D32F2F", fontWeight: 500 }}
// // // // // //                             >
// // // // // //                               +{item.demand_score}%
// // // // // //                             </Typography>
// // // // // //                           </Box>
// // // // // //                         </TableCell>
// // // // // //                         <TableCell>
// // // // // //                           {(item.top_roles || []).join(", ")}
// // // // // //                         </TableCell>
// // // // // //                       </TableRow>
// // // // // //                     ))}
// // // // // //                   </TableBody>
// // // // // //                 </Table>
// // // // // //               </TableContainer>
// // // // // //             </Box>

// // // // // //             <Box
// // // // // //               sx={{
// // // // // //                 backgroundColor: "#F1FFFA",
// // // // // //                 p: 3,
// // // // // //                 borderRadius: "20px",
// // // // // //                 boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
// // // // // //               }}
// // // // // //             >
// // // // // //               <Typography
// // // // // //                 variant="h6"
// // // // // //                 sx={{ fontWeight: "bold", mb: 2, color: "#5A36CC" }}
// // // // // //               >
// // // // // //                 Top Performing Institutions
// // // // // //               </Typography>
// // // // // //               <TableContainer
// // // // // //                 component={Paper}
// // // // // //                 elevation={0}
// // // // // //                 sx={{ backgroundColor: "transparent" }}
// // // // // //               >
// // // // // //                 <Table size="small">
// // // // // //                   <TableHead>
// // // // // //                     <TableRow>
// // // // // //                       <TableCell sx={{ border: 0, color: "#A9A5A5" }}>
// // // // // //                         INSTITUTION
// // // // // //                       </TableCell>
// // // // // //                       <TableCell
// // // // // //                         align="right"
// // // // // //                         sx={{ border: 0, color: "#A9A5A5" }}
// // // // // //                       >
// // // // // //                         GRADUATES
// // // // // //                       </TableCell>
// // // // // //                       <TableCell
// // // // // //                         align="right"
// // // // // //                         sx={{ border: 0, color: "#A9A5A5" }}
// // // // // //                       >
// // // // // //                         PLACEMENTS
// // // // // //                       </TableCell>
// // // // // //                       <TableCell
// // // // // //                         align="right"
// // // // // //                         sx={{ border: 0, color: "#A9A5A5" }}
// // // // // //                       >
// // // // // //                         SUCCESS RATE
// // // // // //                       </TableCell>
// // // // // //                     </TableRow>
// // // // // //                   </TableHead>
// // // // // //                   <TableBody>
// // // // // //                     {institutionData.map((inst, index) => (
// // // // // //                       <TableRow
// // // // // //                         key={`${inst.institution}-${index}`}
// // // // // //                         sx={{
// // // // // //                           backgroundColor: "white",
// // // // // //                           "& > *": { border: 0 },
// // // // // //                         }}
// // // // // //                       >
// // // // // //                         <TableCell>
// // // // // //                           <Box display="flex" alignItems="center">
// // // // // //                             <School sx={{ mr: 1, fontSize: 16 }} />
// // // // // //                             <Typography variant="body2" fontWeight="bold">
// // // // // //                               {inst.institution}
// // // // // //                             </Typography>
// // // // // //                           </Box>
// // // // // //                         </TableCell>
// // // // // //                         <TableCell align="right" sx={{ fontWeight: 500 }}>
// // // // // //                           {inst.graduates}
// // // // // //                         </TableCell>
// // // // // //                         <TableCell align="right" sx={{ fontWeight: 500 }}>
// // // // // //                           {inst.placements}
// // // // // //                         </TableCell>
// // // // // //                         <TableCell align="right">
// // // // // //                           <Chip
// // // // // //                             label={`${inst.rate}%`}
// // // // // //                             size="small"
// // // // // //                             sx={{
// // // // // //                               backgroundColor:
// // // // // //                                 inst.rate >= 90 ? "#E8F5E8" : "#FFF3E0",
// // // // // //                               color: inst.rate >= 90 ? "#2E7D32" : "#F57C00",
// // // // // //                               fontWeight: "bold",
// // // // // //                             }}
// // // // // //                           />
// // // // // //                         </TableCell>
// // // // // //                       </TableRow>
// // // // // //                     ))}
// // // // // //                   </TableBody>
// // // // // //                 </Table>
// // // // // //               </TableContainer>
// // // // // //             </Box>
// // // // // //           </Grid>

// // // // // //           <Grid item xs={12} lg={6}>
// // // // // //             <Box
// // // // // //               sx={{
// // // // // //                 backgroundColor: "#F1FFFA",
// // // // // //                 p: 3,
// // // // // //                 borderRadius: "20px",
// // // // // //                 boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
// // // // // //                 height: "fit-content",
// // // // // //               }}
// // // // // //             >
// // // // // //               <Typography
// // // // // //                 variant="h6"
// // // // // //                 sx={{ fontWeight: "bold", mb: 2, color: "#5A36CC" }}
// // // // // //               >
// // // // // //                 Skill In High Demand
// // // // // //               </Typography>
// // // // // //               <Box
// // // // // //                 sx={{
// // // // // //                   maxHeight: 350,
// // // // // //                   overflowY: "auto",
// // // // // //                   pr: 1,
// // // // // //                   "&::-webkit-scrollbar": { width: "10px" },
// // // // // //                   "&::-webkit-scrollbar-track": {
// // // // // //                     backgroundColor: "transparent",
// // // // // //                   },
// // // // // //                   "&::-webkit-scrollbar-thumb": {
// // // // // //                     backgroundColor: "#ccc",
// // // // // //                     borderRadius: "10px",
// // // // // //                   },
// // // // // //                   "&::-webkit-scrollbar-thumb:hover": {
// // // // // //                     backgroundColor: "#aaa",
// // // // // //                   },
// // // // // //                 }}
// // // // // //               >
// // // // // //                 {skillDemand.map((skill) => (
// // // // // //                   <Paper
// // // // // //                     key={skill.skill}
// // // // // //                     elevation={0}
// // // // // //                     sx={{
// // // // // //                       p: 2,
// // // // // //                       mb: 1.5,
// // // // // //                       borderRadius: "12px",
// // // // // //                       border: "1px solid #EAF2F0",
// // // // // //                       backgroundColor: "white",
// // // // // //                     }}
// // // // // //                   >
// // // // // //                     <Box
// // // // // //                       display="flex"
// // // // // //                       justifyContent="space-between"
// // // // // //                       alignItems="center"
// // // // // //                       mb={1}
// // // // // //                     >
// // // // // //                       <Typography variant="body1" sx={{ fontWeight: "bold" }}>
// // // // // //                         {skill.skill}
// // // // // //                       </Typography>
// // // // // //                       <Chip
// // // // // //                         label={`+${skill.growth}%`}
// // // // // //                         sx={{
// // // // // //                           backgroundColor: "#FFE9D4",
// // // // // //                           color: "#FF9800",
// // // // // //                           height: "24px",
// // // // // //                           fontWeight: "bold",
// // // // // //                           fontSize: "0.75rem",
// // // // // //                         }}
// // // // // //                       />
// // // // // //                     </Box>
// // // // // //                     <LinearProgress
// // // // // //                       variant="determinate"
// // // // // //                       value={skill.demand}
// // // // // //                       sx={{
// // // // // //                         height: 6,
// // // // // //                         borderRadius: 3,
// // // // // //                         mb: 1,
// // // // // //                         backgroundColor: "#E0E0E0",
// // // // // //                         "& .MuiLinearProgress-bar": {
// // // // // //                           backgroundColor: "#4CAF50",
// // // // // //                         },
// // // // // //                       }}
// // // // // //                     />
// // // // // //                     <Typography variant="body2" color="#A9A5A5">
// // // // // //                       {skill.placements.toLocaleString()} Placement
// // // // // //                     </Typography>
// // // // // //                   </Paper>
// // // // // //                 ))}
// // // // // //               </Box>
// // // // // //             </Box>
// // // // // //           </Grid>
// // // // // //         </Grid>
// // // // // //       </Box>
// // // // // //     </Layout>
// // // // // //   );
// // // // // // };

// // // // // // export default Analytics;
// // // // // "use client";

// // // // // import { useState, useEffect } from "react";
// // // // // import {
// // // // //   Grid,
// // // // //   Card,
// // // // //   CardContent,
// // // // //   Typography,
// // // // //   Box,
// // // // //   FormControl,
// // // // //   Select,
// // // // //   MenuItem,
// // // // //   Button,
// // // // //   Table,
// // // // //   TableBody,
// // // // //   TableCell,
// // // // //   TableContainer,
// // // // //   TableHead,
// // // // //   TableRow,
// // // // //   LinearProgress,
// // // // //   Chip,
// // // // //   Paper,
// // // // //   Skeleton,
// // // // //   Stack,
// // // // // } from "@mui/material";
// // // // // import { useTheme, alpha } from "@mui/material/styles";
// // // // // import {
// // // // //   School,
// // // // //   AccessTime,
// // // // //   Public,
// // // // //   FileDownloadOutlined,
// // // // //   KeyboardArrowDown,
// // // // // } from "@mui/icons-material";
// // // // // import Layout from "../common/Layout";
// // // // // import { useNotification } from "../../context/NotificationContext";
// // // // // import apiClient from "../../context/apiClient";

// // // // // const Analytics = () => {
// // // // //   const theme = useTheme();
// // // // //   const { showNotification } = useNotification();
// // // // //   const [timeRange, setTimeRange] = useState("month");
// // // // //   const [region, setRegion] = useState("all");
// // // // //   const [loading, setLoading] = useState(true);

// // // // //   const [globalMetrics, setGlobalMetrics] = useState({
// // // // //     totalTalent: 0,
// // // // //     activePlacements: 0,
// // // // //     totalEmployers: 0,
// // // // //     successRate: 0,
// // // // //     avgPlacementTime: 0,
// // // // //   });
// // // // //   const [regionData, setRegionData] = useState([]);
// // // // //   const [skillDemand, setSkillDemand] = useState([]);
// // // // //   const [institutionData, setInstitutionData] = useState([]);

// // // // //   useEffect(() => {
// // // // //     const fetchDashboardData = async () => {
// // // // //       setLoading(true);
// // // // //       try {
// // // // //         const response = await apiClient.get("/dashboard/global-overview/");
// // // // //         const data = response.data;

// // // // //         setGlobalMetrics({
// // // // //           totalTalent: data.global_ai_talent || 0,
// // // // //           activePlacements: data.active_placements || 0,
// // // // //           totalEmployers: data.global_employer || 0,
// // // // //           successRate: Number.parseFloat(data.success_rate || 0).toFixed(2),
// // // // //           avgPlacementTime: Number.parseFloat(
// // // // //             data.average_days_to_place || 0
// // // // //           ).toFixed(0),
// // // // //         });

// // // // //         const topRolesExamples = [
// // // // //           ["AI Engineer"],
// // // // //           ["Java Developer"],
// // // // //           ["Python", "HTML"],
// // // // //           ["UIUX Designer"],
// // // // //         ];

// // // // //         setRegionData(
// // // // //           (data.placements_by_region || []).map((item, index) => ({
// // // // //             ...item,
// // // // //             avgSalary: `$${Number(item.avg_salary || 0).toLocaleString()}`,
// // // // //             top_roles:
// // // // //               item.top_roles ||
// // // // //               topRolesExamples[index % topRolesExamples.length],
// // // // //           }))
// // // // //         );

// // // // //         const skills = data.skills_in_high_demand || [];
// // // // //         const maxPlacements = Math.max(...skills.map((s) => s.placements), 0);
// // // // //         setSkillDemand(
// // // // //           skills.map((item) => ({
// // // // //             ...item,
// // // // //             demand:
// // // // //               maxPlacements > 0 ? (item.placements / maxPlacements) * 100 : 0,
// // // // //           }))
// // // // //         );

// // // // //         setInstitutionData(
// // // // //           (data.top_performing_institutions || []).map((item) => ({
// // // // //             institution: item.institution_name || "Unknown Institution",
// // // // //             graduates: item.graduates || 0,
// // // // //             placements: item.placements || 0,
// // // // //             rate: Number.parseFloat(item.success_rate || 0).toFixed(0),
// // // // //           }))
// // // // //         );
// // // // //       } catch (error) {
// // // // //         console.error("Failed to fetch dashboard data:", error);
// // // // //         showNotification(
// // // // //           "Failed to load dashboard analytics. Please try again later.",
// // // // //           "error"
// // // // //         );
// // // // //       } finally {
// // // // //         setLoading(false);
// // // // //       }
// // // // //     };

// // // // //     fetchDashboardData();
// // // // //   }, [showNotification]);

// // // // //   const handleExportReport = () => {
// // // // //     showNotification("Analytics report exported successfully!", "success");
// // // // //   };

// // // // //   const kpiCards = [
// // // // //     { title: "TOTAL AI TALENT", value: globalMetrics.totalTalent },
// // // // //     { title: "ACTIVE PLACEMENT", value: globalMetrics.activePlacements },
// // // // //     { title: "GLOBAL EMPLOYERS", value: globalMetrics.totalEmployers },
// // // // //     { title: "SUCCESS RATE", value: `${globalMetrics.successRate}%` },
// // // // //     { title: "AVG DAYS TO PLACE", value: globalMetrics.avgPlacementTime },
// // // // //   ];

// // // // //   const sectionPaperStyles = {
// // // // //     p: 3,
// // // // //     borderRadius: "20px",
// // // // //     boxShadow: theme.shadows[1],
// // // // //     backgroundColor: theme.palette.mint.main,
// // // // //   };

// // // // //   return (
// // // // //     <Layout title="Analytics Dashboard">
// // // // //       <Box
// // // // //         sx={{
// // // // //           p: { xs: 2, md: 3 },
// // // // //           backgroundColor: theme.palette.background.default,
// // // // //         }}
// // // // //       >
// // // // //         <Stack direction="column" spacing={3}>
// // // // //           {/* ROW 1: HEADER PANEL */}
// // // // //           <Paper
// // // // //             elevation={0}
// // // // //             sx={{
// // // // //               ...sectionPaperStyles,
// // // // //               display: "flex",
// // // // //               justifyContent: "space-between",
// // // // //               alignItems: { xs: "flex-start", md: "center" },
// // // // //               flexDirection: { xs: "column", md: "row" },
// // // // //             }}
// // // // //           >
// // // // //             <Box mb={{ xs: 3, md: 0 }}>
// // // // //               <Typography
// // // // //                 variant="h4"
// // // // //                 sx={{ fontWeight: "bold", color: theme.palette.primary.main }}
// // // // //               >
// // // // //                 Analytics
// // // // //               </Typography>
// // // // //               <Typography variant="body1" color="text.secondary">
// // // // //                 Comprehensive insight into AI Talent export Performance
// // // // //               </Typography>
// // // // //             </Box>
// // // // //             <Box
// // // // //               display="flex"
// // // // //               gap={2}
// // // // //               flexWrap="wrap"
// // // // //               justifyContent={{ xs: "flex-start", md: "flex-end" }}
// // // // //             >
// // // // //               <FormControl sx={{ minWidth: 160 }}>
// // // // //                 <Select
// // // // //                   value={timeRange}
// // // // //                   onChange={(e) => setTimeRange(e.target.value)}
// // // // //                   IconComponent={KeyboardArrowDown}
// // // // //                   size="small"
// // // // //                   sx={{
// // // // //                     borderRadius: "50px",
// // // // //                     color: "primary.main",
// // // // //                     "& .MuiOutlinedInput-notchedOutline": {
// // // // //                       borderColor: "primary.main",
// // // // //                     },
// // // // //                     "& .MuiSvgIcon-root": { color: "primary.main" },
// // // // //                   }}
// // // // //                   renderValue={(value) => (
// // // // //                     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
// // // // //                       <AccessTime fontSize="small" />
// // // // //                       <Typography variant="body2">
// // // // //                         {value === "week"
// // // // //                           ? "Last Week"
// // // // //                           : value === "month"
// // // // //                           ? "Last Month"
// // // // //                           : value === "quarter"
// // // // //                           ? "Last Quarter"
// // // // //                           : "Last Year"}
// // // // //                       </Typography>
// // // // //                     </Box>
// // // // //                   )}
// // // // //                 >
// // // // //                   <MenuItem value="week">Last Week</MenuItem>
// // // // //                   <MenuItem value="month">Last Month</MenuItem>
// // // // //                   <MenuItem value="quarter">Last Quarter</MenuItem>
// // // // //                   <MenuItem value="year">Last Year</MenuItem>
// // // // //                 </Select>
// // // // //               </FormControl>
// // // // //               <FormControl sx={{ minWidth: 160 }}>
// // // // //                 <Select
// // // // //                   value={region}
// // // // //                   onChange={(e) => setRegion(e.target.value)}
// // // // //                   IconComponent={KeyboardArrowDown}
// // // // //                   size="small"
// // // // //                   sx={{
// // // // //                     borderRadius: "50px",
// // // // //                     color: "primary.main",
// // // // //                     "& .MuiOutlinedInput-notchedOutline": {
// // // // //                       borderColor: "primary.main",
// // // // //                     },
// // // // //                     "& .MuiSvgIcon-root": { color: "primary.main" },
// // // // //                   }}
// // // // //                   renderValue={(value) => (
// // // // //                     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
// // // // //                       <Public fontSize="small" />
// // // // //                       <Typography variant="body2">
// // // // //                         {value === "all" ? "All Regions" : value}
// // // // //                       </Typography>
// // // // //                     </Box>
// // // // //                   )}
// // // // //                 >
// // // // //                   <MenuItem value="all">All Regions</MenuItem>
// // // // //                   <MenuItem value="UAE">UAE</MenuItem>
// // // // //                   <MenuItem value="USA">USA</MenuItem>
// // // // //                   <MenuItem value="EU">EU</MenuItem>
// // // // //                   <MenuItem value="Singapore">Singapore</MenuItem>
// // // // //                 </Select>
// // // // //               </FormControl>
// // // // //               <Button
// // // // //                 variant="outlined"
// // // // //                 color="primary"
// // // // //                 startIcon={<FileDownloadOutlined />}
// // // // //                 onClick={handleExportReport}
// // // // //                 sx={{
// // // // //                   borderRadius: "50px",
// // // // //                   textTransform: "none",
// // // // //                   height: "40px",
// // // // //                 }}
// // // // //               >
// // // // //                 Export Report
// // // // //               </Button>
// // // // //             </Box>
// // // // //           </Paper>

// // // // //           {/* ROW 2: KPI CARDS */}
// // // // //           <Box display="flex" gap={{ xs: 1.5, md: 2 }}>
// // // // //             {(loading ? [...Array(5)] : kpiCards).map((item, index) => (
// // // // //               <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
// // // // //                 {loading ? (
// // // // //                   <Skeleton
// // // // //                     variant="rectangular"
// // // // //                     width="100%"
// // // // //                     height={120}
// // // // //                     sx={{ borderRadius: "14px" }}
// // // // //                   />
// // // // //                 ) : (
// // // // //                   <Card
// // // // //                     sx={{
// // // // //                       position: "relative",
// // // // //                       backgroundColor: theme.palette.primary.main,
// // // // //                       color: theme.palette.primary.contrastText,
// // // // //                       height: "120px",
// // // // //                       borderRadius: "14px",
// // // // //                       overflow: "hidden",
// // // // //                       boxShadow: theme.shadows[2],
// // // // //                     }}
// // // // //                   >
// // // // //                     <svg
// // // // //                       width="80"
// // // // //                       height="120"
// // // // //                       viewBox="0 0 80 120"
// // // // //                       style={{ position: "absolute", right: 0, top: 0 }}
// // // // //                     >
// // // // //                       <path
// // // // //                         d="M20 0 Q60 30 40 60 Q20 90 60 120 L80 120 L80 0 Z"
// // // // //                         fill={theme.palette.accent.main}
// // // // //                       />
// // // // //                     </svg>
// // // // //                     <CardContent
// // // // //                       sx={{
// // // // //                         position: "relative",
// // // // //                         zIndex: 1,
// // // // //                         p: { xs: 1.5, md: 2 },
// // // // //                       }}
// // // // //                     >
// // // // //                       <Typography
// // // // //                         sx={{
// // // // //                           fontWeight: "bold",
// // // // //                           fontSize: { xs: "0.7rem", md: "0.8rem" },
// // // // //                           textTransform: "uppercase",
// // // // //                           color: alpha(theme.palette.primary.contrastText, 0.8),
// // // // //                           mb: 1,
// // // // //                           lineHeight: 1.2,
// // // // //                         }}
// // // // //                       >
// // // // //                         {item.title}
// // // // //                       </Typography>
// // // // //                       <Typography
// // // // //                         sx={{
// // // // //                           fontWeight: "bold",
// // // // //                           fontSize: { xs: "1.5rem", md: "1.75rem" },
// // // // //                           lineHeight: 1,
// // // // //                         }}
// // // // //                       >
// // // // //                         {item.value}
// // // // //                       </Typography>
// // // // //                     </CardContent>
// // // // //                   </Card>
// // // // //                 )}
// // // // //               </Box>
// // // // //             ))}
// // // // //           </Box>

// // // // //           {/* ROW 3: TWO COLUMNS (SKILLS & INSTITUTIONS) */}
// // // // //           <Box
// // // // //             sx={{
// // // // //               display: "flex",
// // // // //               flexDirection: { xs: "column", lg: "row" },
// // // // //               gap: 3,
// // // // //             }}
// // // // //           >
// // // // //             {/* Column 1: Skills in Demand */}
// // // // //             <Box sx={{ flex: { lg: 1 }, minWidth: 0 }}>
// // // // //               <Paper
// // // // //                 elevation={0}
// // // // //                 sx={{ ...sectionPaperStyles, height: "100%" }}
// // // // //               >
// // // // //                 <Typography
// // // // //                   variant="h6"
// // // // //                   sx={{
// // // // //                     fontWeight: "bold",
// // // // //                     mb: 2,
// // // // //                     color: theme.palette.primary.main,
// // // // //                   }}
// // // // //                 >
// // // // //                   Skill In High Demand
// // // // //                 </Typography>
// // // // //                 <Stack spacing={1.5}>
// // // // //                   {(loading ? [...Array(5)] : skillDemand).map(
// // // // //                     (skill, index) => (
// // // // //                       <Paper
// // // // //                         key={index}
// // // // //                         elevation={0}
// // // // //                         sx={{
// // // // //                           p: 2,
// // // // //                           borderRadius: "12px",
// // // // //                           border: `1px solid ${theme.palette.divider}`,
// // // // //                           backgroundColor: "background.paper",
// // // // //                         }}
// // // // //                       >
// // // // //                         {loading ? (
// // // // //                           <Skeleton height={60} />
// // // // //                         ) : (
// // // // //                           <>
// // // // //                             <Box
// // // // //                               display="flex"
// // // // //                               justifyContent="space-between"
// // // // //                               alignItems="center"
// // // // //                               mb={1}
// // // // //                             >
// // // // //                               <Typography
// // // // //                                 variant="body1"
// // // // //                                 sx={{ fontWeight: "bold" }}
// // // // //                               >
// // // // //                                 {skill.skill}
// // // // //                               </Typography>
// // // // //                               <Chip
// // // // //                                 label={`+${skill.growth}%`}
// // // // //                                 size="small"
// // // // //                                 sx={{
// // // // //                                   backgroundColor: alpha(
// // // // //                                     theme.palette.warning.main,
// // // // //                                     0.2
// // // // //                                   ),
// // // // //                                   color:
// // // // //                                     theme.palette.warning.dark ||
// // // // //                                     theme.palette.warning.main,
// // // // //                                   fontWeight: "bold",
// // // // //                                 }}
// // // // //                               />
// // // // //                             </Box>
// // // // //                             <LinearProgress
// // // // //                               variant="determinate"
// // // // //                               value={skill.demand}
// // // // //                               color="success"
// // // // //                               sx={{
// // // // //                                 height: 6,
// // // // //                                 borderRadius: 3,
// // // // //                                 mb: 1,
// // // // //                                 backgroundColor: "divider",
// // // // //                               }}
// // // // //                             />
// // // // //                             <Typography variant="body2" color="text.secondary">
// // // // //                               {skill.placements.toLocaleString()} Placement
// // // // //                             </Typography>
// // // // //                           </>
// // // // //                         )}
// // // // //                       </Paper>
// // // // //                     )
// // // // //                   )}
// // // // //                 </Stack>
// // // // //               </Paper>
// // // // //             </Box>

// // // // //             {/* Column 2: Top Performing Institutions */}
// // // // //             <Box sx={{ flex: { lg: 2 }, minWidth: 0 }}>
// // // // //               <Paper
// // // // //                 elevation={0}
// // // // //                 sx={{ ...sectionPaperStyles, height: "100%" }}
// // // // //               >
// // // // //                 <Typography
// // // // //                   variant="h6"
// // // // //                   sx={{
// // // // //                     fontWeight: "bold",
// // // // //                     mb: 2,
// // // // //                     color: theme.palette.primary.main,
// // // // //                   }}
// // // // //                 >
// // // // //                   Top Performing Institutions
// // // // //                 </Typography>
// // // // //                 <TableContainer>
// // // // //                   <Table size="small">
// // // // //                     <TableBody>
// // // // //                       {(loading ? [...Array(5)] : institutionData).map(
// // // // //                         (inst, index) => (
// // // // //                           <TableRow
// // // // //                             key={index}
// // // // //                             sx={{ "& td, & th": { border: 0, py: 1.5 } }}
// // // // //                           >
// // // // //                             {loading ? (
// // // // //                               <TableCell colSpan={4}>
// // // // //                                 <Skeleton />
// // // // //                               </TableCell>
// // // // //                             ) : (
// // // // //                               <>
// // // // //                                 <TableCell sx={{ width: "50%" }}>
// // // // //                                   <Box display="flex" alignItems="center">
// // // // //                                     <School
// // // // //                                       sx={{
// // // // //                                         mr: 1.5,
// // // // //                                         fontSize: 18,
// // // // //                                         color: "text.secondary",
// // // // //                                       }}
// // // // //                                     />
// // // // //                                     <Typography
// // // // //                                       variant="body2"
// // // // //                                       fontWeight="bold"
// // // // //                                     >
// // // // //                                       {inst.institution}
// // // // //                                     </Typography>
// // // // //                                   </Box>
// // // // //                                 </TableCell>
// // // // //                                 <TableCell align="center">
// // // // //                                   {inst.graduates}{" "}
// // // // //                                   <Typography
// // // // //                                     variant="caption"
// // // // //                                     color="text.secondary"
// // // // //                                   >
// // // // //                                     Grads
// // // // //                                   </Typography>
// // // // //                                 </TableCell>
// // // // //                                 <TableCell align="center">
// // // // //                                   {inst.placements}{" "}
// // // // //                                   <Typography
// // // // //                                     variant="caption"
// // // // //                                     color="text.secondary"
// // // // //                                   >
// // // // //                                     Hired
// // // // //                                   </Typography>
// // // // //                                 </TableCell>
// // // // //                                 <TableCell align="right">
// // // // //                                   <Chip
// // // // //                                     label={`${inst.rate}%`}
// // // // //                                     size="small"
// // // // //                                     sx={{
// // // // //                                       backgroundColor:
// // // // //                                         inst.rate >= 90
// // // // //                                           ? alpha(
// // // // //                                               theme.palette.success.main,
// // // // //                                               0.15
// // // // //                                             )
// // // // //                                           : alpha(
// // // // //                                               theme.palette.warning.main,
// // // // //                                               0.15
// // // // //                                             ),
// // // // //                                       color:
// // // // //                                         inst.rate >= 90
// // // // //                                           ? theme.palette.success.dark
// // // // //                                           : theme.palette.warning.dark,
// // // // //                                       fontWeight: "bold",
// // // // //                                     }}
// // // // //                                   />
// // // // //                                 </TableCell>
// // // // //                               </>
// // // // //                             )}
// // // // //                           </TableRow>
// // // // //                         )
// // // // //                       )}
// // // // //                     </TableBody>
// // // // //                   </Table>
// // // // //                 </TableContainer>
// // // // //               </Paper>
// // // // //             </Box>
// // // // //           </Box>

// // // // //           {/* ROW 4: REGIONAL PERFORMANCE */}
// // // // //           <Paper elevation={0} sx={sectionPaperStyles}>
// // // // //             <Typography
// // // // //               variant="h6"
// // // // //               sx={{
// // // // //                 fontWeight: "bold",
// // // // //                 mb: 2,
// // // // //                 color: theme.palette.primary.main,
// // // // //               }}
// // // // //             >
// // // // //               Regional Performance
// // // // //             </Typography>
// // // // //             <TableContainer>
// // // // //               <Table>
// // // // //                 <TableHead>
// // // // //                   <TableRow>
// // // // //                     <TableCell>Region</TableCell>
// // // // //                     <TableCell>Placement</TableCell>
// // // // //                     <TableCell>Growth</TableCell>
// // // // //                     <TableCell>Avg Salary</TableCell>
// // // // //                     <TableCell>Demand Score</TableCell>
// // // // //                     <TableCell>Top Roles</TableCell>
// // // // //                   </TableRow>
// // // // //                 </TableHead>
// // // // //                 <TableBody>
// // // // //                   {(loading ? [...Array(4)] : regionData).map((item, index) => (
// // // // //                     <TableRow
// // // // //                       key={index}
// // // // //                       sx={{
// // // // //                         "&:hover": {
// // // // //                           backgroundColor: theme.palette.action.hover,
// // // // //                         },
// // // // //                       }}
// // // // //                     >
// // // // //                       {loading ? (
// // // // //                         <TableCell colSpan={6}>
// // // // //                           <Skeleton />
// // // // //                         </TableCell>
// // // // //                       ) : (
// // // // //                         <>
// // // // //                           <TableCell sx={{ fontWeight: "bold" }}>
// // // // //                             {item.region}
// // // // //                           </TableCell>
// // // // //                           <TableCell
// // // // //                             sx={{ color: "error.main", fontWeight: 500 }}
// // // // //                           >
// // // // //                             {item.placements.toLocaleString()}
// // // // //                           </TableCell>
// // // // //                           <TableCell
// // // // //                             sx={{ color: "success.main", fontWeight: 500 }}
// // // // //                           >
// // // // //                             +{item.growth}%
// // // // //                           </TableCell>
// // // // //                           <TableCell sx={{ fontWeight: "bold" }}>
// // // // //                             {item.avgSalary}
// // // // //                           </TableCell>
// // // // //                           <TableCell>
// // // // //                             <Box display="flex" alignItems="center">
// // // // //                               <LinearProgress
// // // // //                                 variant="determinate"
// // // // //                                 value={item.demand_score}
// // // // //                                 color="success"
// // // // //                                 sx={{
// // // // //                                   width: 60,
// // // // //                                   mr: 1,
// // // // //                                   backgroundColor: "divider",
// // // // //                                 }}
// // // // //                               />
// // // // //                               <Typography
// // // // //                                 variant="caption"
// // // // //                                 sx={{ color: "success.main", fontWeight: 500 }}
// // // // //                               >
// // // // //                                 {item.demand_score}%
// // // // //                               </Typography>
// // // // //                             </Box>
// // // // //                           </TableCell>
// // // // //                           <TableCell>
// // // // //                             <Typography variant="body2">
// // // // //                               {(item.top_roles || []).join(", ")}
// // // // //                             </Typography>
// // // // //                           </TableCell>
// // // // //                         </>
// // // // //                       )}
// // // // //                     </TableRow>
// // // // //                   ))}
// // // // //                 </TableBody>
// // // // //               </Table>
// // // // //             </TableContainer>
// // // // //           </Paper>
// // // // //         </Stack>
// // // // //       </Box>
// // // // //     </Layout>
// // // // //   );
// // // // // };

// // // // // export default Analytics;
// // // // "use client";

// // // // import { useState, useEffect } from "react";
// // // // import {
// // // //   Grid,
// // // //   Card,
// // // //   CardContent,
// // // //   Typography,
// // // //   Box,
// // // //   FormControl,
// // // //   Select,
// // // //   MenuItem,
// // // //   Button,
// // // //   Table,
// // // //   TableBody,
// // // //   TableCell,
// // // //   TableContainer,
// // // //   TableHead,
// // // //   TableRow,
// // // //   LinearProgress,
// // // //   Chip,
// // // //   Paper,
// // // //   Skeleton,
// // // //   Stack,
// // // //   TablePagination, // 1. IMPORT PAGINATION
// // // // } from "@mui/material";
// // // // import { useTheme, alpha } from "@mui/material/styles";
// // // // import {
// // // //   School,
// // // //   AccessTime,
// // // //   Public,
// // // //   FileDownloadOutlined,
// // // //   KeyboardArrowDown,
// // // // } from "@mui/icons-material";
// // // // import Layout from "../common/Layout";
// // // // import { useNotification } from "../../context/NotificationContext";
// // // // import apiClient from "../../context/apiClient";

// // // // const Analytics = () => {
// // // //   const theme = useTheme();
// // // //   const { showNotification } = useNotification();
// // // //   const [timeRange, setTimeRange] = useState("month");
// // // //   const [region, setRegion] = useState("all");
// // // //   const [loading, setLoading] = useState(true);

// // // //   // --- STATE FOR PAGINATION ---
// // // //   const [page, setPage] = useState(0);
// // // //   const [rowsPerPage, setRowsPerPage] = useState(5);

// // // //   const [globalMetrics, setGlobalMetrics] = useState({
// // // //     totalTalent: 0,
// // // //     activePlacements: 0,
// // // //     totalEmployers: 0,
// // // //     successRate: 0,
// // // //     avgPlacementTime: 0,
// // // //   });
// // // //   const [regionData, setRegionData] = useState([]);
// // // //   const [skillDemand, setSkillDemand] = useState([]);
// // // //   const [institutionData, setInstitutionData] = useState([]);

// // // //   // --- HANDLERS FOR PAGINATION ---
// // // //   const handleChangePage = (event, newPage) => {
// // // //     setPage(newPage);
// // // //   };

// // // //   const handleChangeRowsPerPage = (event) => {
// // // //     setRowsPerPage(parseInt(event.target.value, 10));
// // // //     setPage(0); // Reset to first page
// // // //   };

// // // //   useEffect(() => {
// // // //     const fetchDashboardData = async () => {
// // // //       setLoading(true);
// // // //       try {
// // // //         const response = await apiClient.get("/dashboard/global-overview/");
// // // //         const data = response.data;

// // // //         setGlobalMetrics({
// // // //           totalTalent: data.global_ai_talent || 0,
// // // //           activePlacements: data.active_placements || 0,
// // // //           totalEmployers: data.global_employer || 0,
// // // //           successRate: Number.parseFloat(data.success_rate || 0).toFixed(2),
// // // //           avgPlacementTime: Number.parseFloat(
// // // //             data.average_days_to_place || 0
// // // //           ).toFixed(0),
// // // //         });

// // // //         const topRolesExamples = [
// // // //           ["AI Engineer"],
// // // //           ["Java Developer"],
// // // //           ["Python", "HTML"],
// // // //           ["UIUX Designer"],
// // // //         ];

// // // //         setRegionData(
// // // //           (data.placements_by_region || []).map((item, index) => ({
// // // //             ...item,
// // // //             avgSalary: `$${Number(item.avg_salary || 0).toLocaleString()}`,
// // // //             top_roles:
// // // //               item.top_roles ||
// // // //               topRolesExamples[index % topRolesExamples.length],
// // // //           }))
// // // //         );

// // // //         const skills = data.skills_in_high_demand || [];
// // // //         const maxPlacements = Math.max(...skills.map((s) => s.placements), 0);
// // // //         setSkillDemand(
// // // //           skills.map((item) => ({
// // // //             ...item,
// // // //             demand:
// // // //               maxPlacements > 0 ? (item.placements / maxPlacements) * 100 : 0,
// // // //           }))
// // // //         );

// // // //         setInstitutionData(
// // // //           (data.top_performing_institutions || []).map((item) => ({
// // // //             institution: item.institution_name || "Unknown Institution",
// // // //             graduates: item.graduates || 0,
// // // //             placements: item.placements || 0,
// // // //             rate: Number.parseFloat(item.success_rate || 0).toFixed(0),
// // // //           }))
// // // //         );
// // // //       } catch (error) {
// // // //         console.error("Failed to fetch dashboard data:", error);
// // // //         showNotification(
// // // //           "Failed to load dashboard analytics. Please try again later.",
// // // //           "error"
// // // //         );
// // // //       } finally {
// // // //         setLoading(false);
// // // //       }
// // // //     };

// // // //     fetchDashboardData();
// // // //   }, [showNotification]);

// // // //   const handleExportReport = () => {
// // // //     showNotification("Analytics report exported successfully!", "success");
// // // //   };

// // // //   const kpiCards = [
// // // //     { title: "TOTAL AI TALENT", value: globalMetrics.totalTalent },
// // // //     { title: "ACTIVE PLACEMENT", value: globalMetrics.activePlacements },
// // // //     { title: "GLOBAL EMPLOYERS", value: globalMetrics.totalEmployers },
// // // //     { title: "SUCCESS RATE", value: `${globalMetrics.successRate}%` },
// // // //     { title: "AVG DAYS TO PLACE", value: globalMetrics.avgPlacementTime },
// // // //   ];

// // // //   const sectionPaperStyles = {
// // // //     p: 3,
// // // //     borderRadius: "20px",
// // // //     boxShadow: theme.shadows[1],
// // // //     backgroundColor: theme.palette.mint.main,
// // // //   };

// // // //   return (
// // // //     <Layout title="Analytics Dashboard">
// // // //       <Box
// // // //         sx={{
// // // //           p: { xs: 2, md: 3 },
// // // //           backgroundColor: theme.palette.background.default,
// // // //         }}
// // // //       >
// // // //         <Stack direction="column" spacing={3}>
// // // //           {/* ROW 1: HEADER PANEL */}
// // // //           <Paper
// // // //             elevation={0}
// // // //             sx={{
// // // //               ...sectionPaperStyles,
// // // //               display: "flex",
// // // //               justifyContent: "space-between",
// // // //               alignItems: { xs: "flex-start", md: "center" },
// // // //               flexDirection: { xs: "column", md: "row" },
// // // //             }}
// // // //           >
// // // //             <Box mb={{ xs: 3, md: 0 }}>
// // // //               <Typography
// // // //                 variant="h4"
// // // //                 sx={{ fontWeight: "bold", color: theme.palette.primary.main }}
// // // //               >
// // // //                 Analytics
// // // //               </Typography>
// // // //               <Typography variant="body1" color="text.secondary">
// // // //                 Comprehensive insight into AI Talent export Performance
// // // //               </Typography>
// // // //             </Box>
// // // //             <Box
// // // //               display="flex"
// // // //               gap={2}
// // // //               flexWrap="wrap"
// // // //               justifyContent={{ xs: "flex-start", md: "flex-end" }}
// // // //             >
// // // //               <FormControl sx={{ minWidth: 160 }}>
// // // //                 <Select
// // // //                   value={timeRange}
// // // //                   onChange={(e) => setTimeRange(e.target.value)}
// // // //                   IconComponent={KeyboardArrowDown}
// // // //                   size="small"
// // // //                   sx={{
// // // //                     borderRadius: "50px",
// // // //                     color: "primary.main",
// // // //                     "& .MuiOutlinedInput-notchedOutline": {
// // // //                       borderColor: "primary.main",
// // // //                     },
// // // //                     "& .MuiSvgIcon-root": { color: "primary.main" },
// // // //                   }}
// // // //                   renderValue={(value) => (
// // // //                     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
// // // //                       <AccessTime fontSize="small" />
// // // //                       <Typography variant="body2">
// // // //                         {value === "week"
// // // //                           ? "Last Week"
// // // //                           : value === "month"
// // // //                           ? "Last Month"
// // // //                           : value === "quarter"
// // // //                           ? "Last Quarter"
// // // //                           : "Last Year"}
// // // //                       </Typography>
// // // //                     </Box>
// // // //                   )}
// // // //                 >
// // // //                   <MenuItem value="week">Last Week</MenuItem>
// // // //                   <MenuItem value="month">Last Month</MenuItem>
// // // //                   <MenuItem value="quarter">Last Quarter</MenuItem>
// // // //                   <MenuItem value="year">Last Year</MenuItem>
// // // //                 </Select>
// // // //               </FormControl>
// // // //               <FormControl sx={{ minWidth: 160 }}>
// // // //                 <Select
// // // //                   value={region}
// // // //                   onChange={(e) => setRegion(e.target.value)}
// // // //                   IconComponent={KeyboardArrowDown}
// // // //                   size="small"
// // // //                   sx={{
// // // //                     borderRadius: "50px",
// // // //                     color: "primary.main",
// // // //                     "& .MuiOutlinedInput-notchedOutline": {
// // // //                       borderColor: "primary.main",
// // // //                     },
// // // //                     "& .MuiSvgIcon-root": { color: "primary.main" },
// // // //                   }}
// // // //                   renderValue={(value) => (
// // // //                     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
// // // //                       <Public fontSize="small" />
// // // //                       <Typography variant="body2">
// // // //                         {value === "all" ? "All Regions" : value}
// // // //                       </Typography>
// // // //                     </Box>
// // // //                   )}
// // // //                 >
// // // //                   <MenuItem value="all">All Regions</MenuItem>
// // // //                   <MenuItem value="UAE">UAE</MenuItem>
// // // //                   <MenuItem value="USA">USA</MenuItem>
// // // //                   <MenuItem value="EU">EU</MenuItem>
// // // //                   <MenuItem value="Singapore">Singapore</MenuItem>
// // // //                 </Select>
// // // //               </FormControl>
// // // //               <Button
// // // //                 variant="outlined"
// // // //                 color="primary"
// // // //                 startIcon={<FileDownloadOutlined />}
// // // //                 onClick={handleExportReport}
// // // //                 sx={{
// // // //                   borderRadius: "50px",
// // // //                   textTransform: "none",
// // // //                   height: "40px",
// // // //                 }}
// // // //               >
// // // //                 Export Report
// // // //               </Button>
// // // //             </Box>
// // // //           </Paper>

// // // //           {/* ROW 2: KPI CARDS */}
// // // //           <Box display="flex" gap={{ xs: 1.5, md: 2 }}>
// // // //             {(loading ? [...Array(5)] : kpiCards).map((item, index) => (
// // // //               <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
// // // //                 {loading ? (
// // // //                   <Skeleton
// // // //                     variant="rectangular"
// // // //                     width="100%"
// // // //                     height={120}
// // // //                     sx={{ borderRadius: "14px" }}
// // // //                   />
// // // //                 ) : (
// // // //                   <Card
// // // //                     sx={{
// // // //                       position: "relative",
// // // //                       backgroundColor: theme.palette.primary.main,
// // // //                       color: theme.palette.primary.contrastText,
// // // //                       height: "120px",
// // // //                       borderRadius: "14px",
// // // //                       overflow: "hidden",
// // // //                       boxShadow: theme.shadows[2],
// // // //                     }}
// // // //                   >
// // // //                     <svg
// // // //                       width="80"
// // // //                       height="120"
// // // //                       viewBox="0 0 80 120"
// // // //                       style={{ position: "absolute", right: 0, top: 0 }}
// // // //                     >
// // // //                       <path
// // // //                         d="M20 0 Q60 30 40 60 Q20 90 60 120 L80 120 L80 0 Z"
// // // //                         fill={theme.palette.accent.main}
// // // //                       />
// // // //                     </svg>
// // // //                     <CardContent
// // // //                       sx={{
// // // //                         position: "relative",
// // // //                         zIndex: 1,
// // // //                         p: { xs: 1.5, md: 2 },
// // // //                       }}
// // // //                     >
// // // //                       <Typography
// // // //                         sx={{
// // // //                           fontWeight: "bold",
// // // //                           fontSize: { xs: "0.7rem", md: "0.8rem" },
// // // //                           textTransform: "uppercase",
// // // //                           color: alpha(theme.palette.primary.contrastText, 0.8),
// // // //                           mb: 1,
// // // //                           lineHeight: 1.2,
// // // //                         }}
// // // //                       >
// // // //                         {item.title}
// // // //                       </Typography>
// // // //                       <Typography
// // // //                         sx={{
// // // //                           fontWeight: "bold",
// // // //                           fontSize: { xs: "1.5rem", md: "1.75rem" },
// // // //                           lineHeight: 1,
// // // //                         }}
// // // //                       >
// // // //                         {item.value}
// // // //                       </Typography>
// // // //                     </CardContent>
// // // //                   </Card>
// // // //                 )}
// // // //               </Box>
// // // //             ))}
// // // //           </Box>

// // // //           {/* ROW 3: TWO COLUMNS (SKILLS & INSTITUTIONS) */}
// // // //           <Box
// // // //             sx={{
// // // //               display: "flex",
// // // //               flexDirection: { xs: "column", md: "row" },
// // // //               gap: 3,
// // // //             }}
// // // //           >
// // // //             {/* Column 1: Skills in Demand */}
// // // //             <Box sx={{ flex: { md: 1 }, minWidth: 0 }}>
// // // //               <Paper
// // // //                 elevation={0}
// // // //                 sx={{
// // // //                   ...sectionPaperStyles,
// // // //                   height: "100%",
// // // //                   display: "flex",
// // // //                   flexDirection: "column",
// // // //                 }}
// // // //               >
// // // //                 <Typography
// // // //                   variant="h6"
// // // //                   sx={{
// // // //                     fontWeight: "bold",
// // // //                     mb: 2,
// // // //                     color: theme.palette.primary.main,
// // // //                   }}
// // // //                 >
// // // //                   Skill In High Demand
// // // //                 </Typography>
// // // //                 {/* 2. ADDED SCROLLABLE CONTAINER */}
// // // //                 <Box sx={{ maxHeight: 350, overflowY: "auto", pr: 1 }}>
// // // //                   <Stack spacing={1.5}>
// // // //                     {(loading ? [...Array(5)] : skillDemand).map(
// // // //                       (skill, index) => (
// // // //                         <Paper
// // // //                           key={index}
// // // //                           elevation={0}
// // // //                           sx={{
// // // //                             p: 2,
// // // //                             borderRadius: "12px",
// // // //                             border: `1px solid ${theme.palette.divider}`,
// // // //                             backgroundColor: "background.paper",
// // // //                           }}
// // // //                         >
// // // //                           {loading ? (
// // // //                             <Skeleton height={60} />
// // // //                           ) : (
// // // //                             <>
// // // //                               <Box
// // // //                                 display="flex"
// // // //                                 justifyContent="space-between"
// // // //                                 alignItems="center"
// // // //                                 mb={1}
// // // //                               >
// // // //                                 <Typography
// // // //                                   variant="body1"
// // // //                                   sx={{ fontWeight: "bold" }}
// // // //                                 >
// // // //                                   {skill.skill}
// // // //                                 </Typography>
// // // //                                 <Chip
// // // //                                   label={`+${skill.growth}%`}
// // // //                                   size="small"
// // // //                                   sx={{
// // // //                                     backgroundColor: alpha(
// // // //                                       theme.palette.warning.main,
// // // //                                       0.2
// // // //                                     ),
// // // //                                     color:
// // // //                                       theme.palette.warning.dark ||
// // // //                                       theme.palette.warning.main,
// // // //                                     fontWeight: "bold",
// // // //                                   }}
// // // //                                 />
// // // //                               </Box>
// // // //                               <LinearProgress
// // // //                                 variant="determinate"
// // // //                                 value={skill.demand}
// // // //                                 color="success"
// // // //                                 sx={{
// // // //                                   height: 6,
// // // //                                   borderRadius: 3,
// // // //                                   mb: 1,
// // // //                                   backgroundColor: "divider",
// // // //                                 }}
// // // //                               />
// // // //                               <Typography
// // // //                                 variant="body2"
// // // //                                 color="text.secondary"
// // // //                               >
// // // //                                 {skill.placements.toLocaleString()} Placement
// // // //                               </Typography>
// // // //                             </>
// // // //                           )}
// // // //                         </Paper>
// // // //                       )
// // // //                     )}
// // // //                   </Stack>
// // // //                 </Box>
// // // //               </Paper>
// // // //             </Box>

// // // //             {/* Column 2: Top Performing Institutions */}
// // // //             <Box sx={{ flex: { md: 2 }, minWidth: 0 }}>
// // // //               <Paper
// // // //                 elevation={0}
// // // //                 sx={{
// // // //                   ...sectionPaperStyles,
// // // //                   height: "100%",
// // // //                   display: "flex",
// // // //                   flexDirection: "column",
// // // //                 }}
// // // //               >
// // // //                 <Typography
// // // //                   variant="h6"
// // // //                   sx={{
// // // //                     fontWeight: "bold",
// // // //                     mb: 2,
// // // //                     color: theme.palette.primary.main,
// // // //                   }}
// // // //                 >
// // // //                   Top Performing Institutions
// // // //                 </Typography>
// // // //                 <TableContainer>
// // // //                   <Table size="small">
// // // //                     <TableBody>
// // // //                       {/* 3. APPLY PAGINATION SLICING */}
// // // //                       {(loading
// // // //                         ? [...Array(rowsPerPage)]
// // // //                         : institutionData.slice(
// // // //                             page * rowsPerPage,
// // // //                             page * rowsPerPage + rowsPerPage
// // // //                           )
// // // //                       ).map((inst, index) => (
// // // //                         <TableRow
// // // //                           key={index}
// // // //                           sx={{ "& td, & th": { border: 0, py: 1.5 } }}
// // // //                         >
// // // //                           {loading ? (
// // // //                             <TableCell colSpan={4}>
// // // //                               <Skeleton />
// // // //                             </TableCell>
// // // //                           ) : (
// // // //                             <>
// // // //                               <TableCell sx={{ width: "50%" }}>
// // // //                                 <Box display="flex" alignItems="center">
// // // //                                   <School
// // // //                                     sx={{
// // // //                                       mr: 1.5,
// // // //                                       fontSize: 18,
// // // //                                       color: "text.secondary",
// // // //                                     }}
// // // //                                   />
// // // //                                   <Typography variant="body2" fontWeight="bold">
// // // //                                     {inst.institution}
// // // //                                   </Typography>
// // // //                                 </Box>
// // // //                               </TableCell>
// // // //                               <TableCell align="center">
// // // //                                 {inst.graduates}{" "}
// // // //                                 <Typography
// // // //                                   variant="caption"
// // // //                                   color="text.secondary"
// // // //                                 >
// // // //                                   Grads
// // // //                                 </Typography>
// // // //                               </TableCell>
// // // //                               <TableCell align="center">
// // // //                                 {inst.placements}{" "}
// // // //                                 <Typography
// // // //                                   variant="caption"
// // // //                                   color="text.secondary"
// // // //                                 >
// // // //                                   Hired
// // // //                                 </Typography>
// // // //                               </TableCell>
// // // //                               <TableCell align="right">
// // // //                                 <Chip
// // // //                                   label={`${inst.rate}%`}
// // // //                                   size="small"
// // // //                                   sx={{
// // // //                                     backgroundColor:
// // // //                                       inst.rate >= 90
// // // //                                         ? alpha(
// // // //                                             theme.palette.success.main,
// // // //                                             0.15
// // // //                                           )
// // // //                                         : alpha(
// // // //                                             theme.palette.warning.main,
// // // //                                             0.15
// // // //                                           ),
// // // //                                     color:
// // // //                                       inst.rate >= 90
// // // //                                         ? theme.palette.success.dark
// // // //                                         : theme.palette.warning.dark,
// // // //                                     fontWeight: "bold",
// // // //                                   }}
// // // //                                 />
// // // //                               </TableCell>
// // // //                             </>
// // // //                           )}
// // // //                         </TableRow>
// // // //                       ))}
// // // //                     </TableBody>
// // // //                   </Table>
// // // //                 </TableContainer>
// // // //                 {/* 3. ADD PAGINATION COMPONENT */}
// // // //                 <TablePagination
// // // //                   component="div"
// // // //                   count={institutionData.length}
// // // //                   page={page}
// // // //                   onPageChange={handleChangePage}
// // // //                   rowsPerPage={rowsPerPage}
// // // //                   onRowsPerPageChange={handleChangeRowsPerPage}
// // // //                   rowsPerPageOptions={[5, 10, 25]}
// // // //                   sx={{ mt: "auto", pt: 1 }} // Push to the bottom
// // // //                 />
// // // //               </Paper>
// // // //             </Box>
// // // //           </Box>

// // // //           {/* ROW 4: REGIONAL PERFORMANCE */}
// // // //           <Paper elevation={0} sx={sectionPaperStyles}>
// // // //             <Typography
// // // //               variant="h6"
// // // //               sx={{
// // // //                 fontWeight: "bold",
// // // //                 mb: 2,
// // // //                 color: theme.palette.primary.main,
// // // //               }}
// // // //             >
// // // //               Regional Performance
// // // //             </Typography>
// // // //             <TableContainer>
// // // //               <Table>
// // // //                 <TableHead>
// // // //                   <TableRow>
// // // //                     <TableCell>Region</TableCell>
// // // //                     <TableCell>Placement</TableCell>
// // // //                     <TableCell>Growth</TableCell>
// // // //                     <TableCell>Avg Salary</TableCell>
// // // //                     <TableCell>Demand Score</TableCell>
// // // //                     <TableCell>Top Roles</TableCell>
// // // //                   </TableRow>
// // // //                 </TableHead>
// // // //                 <TableBody>
// // // //                   {(loading ? [...Array(4)] : regionData).map((item, index) => (
// // // //                     <TableRow
// // // //                       key={index}
// // // //                       sx={{
// // // //                         "&:hover": {
// // // //                           backgroundColor: theme.palette.action.hover,
// // // //                         },
// // // //                       }}
// // // //                     >
// // // //                       {loading ? (
// // // //                         <TableCell colSpan={6}>
// // // //                           <Skeleton />
// // // //                         </TableCell>
// // // //                       ) : (
// // // //                         <>
// // // //                           <TableCell sx={{ fontWeight: "bold" }}>
// // // //                             {item.region}
// // // //                           </TableCell>
// // // //                           <TableCell
// // // //                             sx={{ color: "error.main", fontWeight: 500 }}
// // // //                           >
// // // //                             {item.placements.toLocaleString()}
// // // //                           </TableCell>
// // // //                           <TableCell
// // // //                             sx={{ color: "success.main", fontWeight: 500 }}
// // // //                           >
// // // //                             +{item.growth}%
// // // //                           </TableCell>
// // // //                           <TableCell sx={{ fontWeight: "bold" }}>
// // // //                             {item.avgSalary}
// // // //                           </TableCell>
// // // //                           <TableCell>
// // // //                             <Box display="flex" alignItems="center">
// // // //                               <LinearProgress
// // // //                                 variant="determinate"
// // // //                                 value={item.demand_score}
// // // //                                 color="success"
// // // //                                 sx={{
// // // //                                   width: 60,
// // // //                                   mr: 1,
// // // //                                   backgroundColor: "divider",
// // // //                                 }}
// // // //                               />
// // // //                               <Typography
// // // //                                 variant="caption"
// // // //                                 sx={{ color: "success.main", fontWeight: 500 }}
// // // //                               >
// // // //                                 {item.demand_score}%
// // // //                               </Typography>
// // // //                             </Box>
// // // //                           </TableCell>
// // // //                           <TableCell>
// // // //                             <Typography variant="body2">
// // // //                               {(item.top_roles || []).join(", ")}
// // // //                             </Typography>
// // // //                           </TableCell>
// // // //                         </>
// // // //                       )}
// // // //                     </TableRow>
// // // //                   ))}
// // // //                 </TableBody>
// // // //               </Table>
// // // //             </TableContainer>
// // // //           </Paper>
// // // //         </Stack>
// // // //       </Box>
// // // //     </Layout>
// // // //   );
// // // // };

// // // // export default Analytics;
// // // "use client";

// // // import { useState, useEffect } from "react";
// // // import {
// // //   Grid,
// // //   Card,
// // //   CardContent,
// // //   Typography,
// // //   Box,
// // //   FormControl,
// // //   Select,
// // //   MenuItem,
// // //   Button,
// // //   Table,
// // //   TableBody,
// // //   TableCell,
// // //   TableContainer,
// // //   TableHead,
// // //   TableRow,
// // //   LinearProgress,
// // //   Chip,
// // //   Paper,
// // //   Skeleton,
// // //   Stack,
// // //   TablePagination,
// // // } from "@mui/material";
// // // import { useTheme, alpha } from "@mui/material/styles";
// // // import {
// // //   School,
// // //   AccessTime,
// // //   Public,
// // //   FileDownloadOutlined,
// // //   KeyboardArrowDown,
// // // } from "@mui/icons-material";
// // // import Layout from "../common/Layout";
// // // import { useNotification } from "../../context/NotificationContext";
// // // import apiClient from "../../context/apiClient";

// // // const Analytics = () => {
// // //   const theme = useTheme();
// // //   const { showNotification } = useNotification();
// // //   const [timeRange, setTimeRange] = useState("month");
// // //   const [region, setRegion] = useState("all");
// // //   const [loading, setLoading] = useState(true);

// // //   // --- STATE FOR INSTITUTIONS PAGINATION ---
// // //   const [page, setPage] = useState(0);
// // //   const [rowsPerPage, setRowsPerPage] = useState(5);

// // //   // --- STATE FOR REGIONAL PERFORMANCE PAGINATION ---
// // //   const [regionalPage, setRegionalPage] = useState(0);
// // //   const [regionalRowsPerPage, setRegionalRowsPerPage] = useState(5);

// // //   const [globalMetrics, setGlobalMetrics] = useState({
// // //     totalTalent: 0,
// // //     activePlacements: 0,
// // //     totalEmployers: 0,
// // //     successRate: 0,
// // //     avgPlacementTime: 0,
// // //   });
// // //   const [regionData, setRegionData] = useState([]);
// // //   const [skillDemand, setSkillDemand] = useState([]);
// // //   const [institutionData, setInstitutionData] = useState([]);

// // //   // --- HANDLERS FOR INSTITUTIONS PAGINATION ---
// // //   const handleChangePage = (event, newPage) => {
// // //     setPage(newPage);
// // //   };

// // //   const handleChangeRowsPerPage = (event) => {
// // //     setRowsPerPage(parseInt(event.target.value, 10));
// // //     setPage(0);
// // //   };

// // //   // --- HANDLERS FOR REGIONAL PERFORMANCE PAGINATION ---
// // //   const handleChangeRegionalPage = (event, newPage) => {
// // //     setRegionalPage(newPage);
// // //   };

// // //   const handleChangeRegionalRowsPerPage = (event) => {
// // //     setRegionalRowsPerPage(parseInt(event.target.value, 10));
// // //     setRegionalPage(0);
// // //   };

// // //   useEffect(() => {
// // //     const fetchDashboardData = async () => {
// // //       setLoading(true);
// // //       try {
// // //         const response = await apiClient.get("/dashboard/global-overview/");
// // //         const data = response.data;

// // //         setGlobalMetrics({
// // //           totalTalent: data.global_ai_talent || 0,
// // //           activePlacements: data.active_placements || 0,
// // //           totalEmployers: data.global_employer || 0,
// // //           successRate: Number.parseFloat(data.success_rate || 0).toFixed(2),
// // //           avgPlacementTime: Number.parseFloat(
// // //             data.average_days_to_place || 0
// // //           ).toFixed(0),
// // //         });

// // //         const topRolesExamples = [
// // //           ["AI Engineer"],
// // //           ["Java Developer"],
// // //           ["Python", "HTML"],
// // //           ["UIUX Designer"],
// // //         ];

// // //         setRegionData(
// // //           (data.placements_by_region || []).map((item, index) => ({
// // //             ...item,
// // //             avgSalary: `$${Number(item.avg_salary || 0).toLocaleString()}`,
// // //             top_roles:
// // //               item.top_roles ||
// // //               topRolesExamples[index % topRolesExamples.length],
// // //           }))
// // //         );

// // //         const skills = data.skills_in_high_demand || [];
// // //         const maxPlacements = Math.max(...skills.map((s) => s.placements), 0);
// // //         setSkillDemand(
// // //           skills.map((item) => ({
// // //             ...item,
// // //             demand:
// // //               maxPlacements > 0 ? (item.placements / maxPlacements) * 100 : 0,
// // //           }))
// // //         );

// // //         setInstitutionData(
// // //           (data.top_performing_institutions || []).map((item) => ({
// // //             institution: item.institution_name || "Unknown Institution",
// // //             graduates: item.graduates || 0,
// // //             placements: item.placements || 0,
// // //             rate: Number.parseFloat(item.success_rate || 0).toFixed(0),
// // //           }))
// // //         );
// // //       } catch (error) {
// // //         console.error("Failed to fetch dashboard data:", error);
// // //         showNotification(
// // //           "Failed to load dashboard analytics. Please try again later.",
// // //           "error"
// // //         );
// // //       } finally {
// // //         setLoading(false);
// // //       }
// // //     };

// // //     fetchDashboardData();
// // //   }, [showNotification]);

// // //   const handleExportReport = () => {
// // //     showNotification("Analytics report exported successfully!", "success");
// // //   };

// // //   const kpiCards = [
// // //     { title: "TOTAL AI TALENT", value: globalMetrics.totalTalent },
// // //     { title: "ACTIVE PLACEMENT", value: globalMetrics.activePlacements },
// // //     { title: "GLOBAL EMPLOYERS", value: globalMetrics.totalEmployers },
// // //     { title: "SUCCESS RATE", value: `${globalMetrics.successRate}%` },
// // //     { title: "AVG DAYS TO PLACE", value: globalMetrics.avgPlacementTime },
// // //   ];

// // //   const sectionPaperStyles = {
// // //     p: 3,
// // //     borderRadius: "20px",
// // //     boxShadow: theme.shadows[1],
// // //     backgroundColor: theme.palette.mint.main,
// // //   };

// // //   return (
// // //     <Layout title="Analytics Dashboard">
// // //       <Box
// // //         sx={{
// // //           p: { xs: 2, md: 3 },
// // //           backgroundColor: theme.palette.background.default,
// // //         }}
// // //       >
// // //         <Stack direction="column" spacing={3}>
// // //           {/* ROW 1: HEADER PANEL */}
// // //           <Paper
// // //             elevation={0}
// // //             sx={{
// // //               ...sectionPaperStyles,
// // //               display: "flex",
// // //               justifyContent: "space-between",
// // //               alignItems: { xs: "flex-start", md: "center" },
// // //               flexDirection: { xs: "column", md: "row" },
// // //             }}
// // //           >
// // //             <Box mb={{ xs: 3, md: 0 }}>
// // //               <Typography
// // //                 variant="h4"
// // //                 sx={{ fontWeight: "bold", color: theme.palette.primary.main }}
// // //               >
// // //                 Analytics
// // //               </Typography>
// // //               <Typography variant="body1" color="text.secondary">
// // //                 Comprehensive insight into AI Talent export Performance
// // //               </Typography>
// // //             </Box>
// // //             <Box
// // //               display="flex"
// // //               gap={2}
// // //               flexWrap="wrap"
// // //               justifyContent={{ xs: "flex-start", md: "flex-end" }}
// // //             >
// // //               <FormControl sx={{ minWidth: 160 }}>
// // //                 <Select
// // //                   value={timeRange}
// // //                   onChange={(e) => setTimeRange(e.target.value)}
// // //                   IconComponent={KeyboardArrowDown}
// // //                   size="small"
// // //                   sx={{
// // //                     borderRadius: "50px",
// // //                     color: "primary.main",
// // //                     "& .MuiOutlinedInput-notchedOutline": {
// // //                       borderColor: "primary.main",
// // //                     },
// // //                     "& .MuiSvgIcon-root": { color: "primary.main" },
// // //                   }}
// // //                   renderValue={(value) => (
// // //                     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
// // //                       <AccessTime fontSize="small" />
// // //                       <Typography variant="body2">
// // //                         {value === "week"
// // //                           ? "Last Week"
// // //                           : value === "month"
// // //                           ? "Last Month"
// // //                           : value === "quarter"
// // //                           ? "Last Quarter"
// // //                           : "Last Year"}
// // //                       </Typography>
// // //                     </Box>
// // //                   )}
// // //                 >
// // //                   <MenuItem value="week">Last Week</MenuItem>
// // //                   <MenuItem value="month">Last Month</MenuItem>
// // //                   <MenuItem value="quarter">Last Quarter</MenuItem>
// // //                   <MenuItem value="year">Last Year</MenuItem>
// // //                 </Select>
// // //               </FormControl>
// // //               <FormControl sx={{ minWidth: 160 }}>
// // //                 <Select
// // //                   value={region}
// // //                   onChange={(e) => setRegion(e.target.value)}
// // //                   IconComponent={KeyboardArrowDown}
// // //                   size="small"
// // //                   sx={{
// // //                     borderRadius: "50px",
// // //                     color: "primary.main",
// // //                     "& .MuiOutlinedInput-notchedOutline": {
// // //                       borderColor: "primary.main",
// // //                     },
// // //                     "& .MuiSvgIcon-root": { color: "primary.main" },
// // //                   }}
// // //                   renderValue={(value) => (
// // //                     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
// // //                       <Public fontSize="small" />
// // //                       <Typography variant="body2">
// // //                         {value === "all" ? "All Regions" : value}
// // //                       </Typography>
// // //                     </Box>
// // //                   )}
// // //                 >
// // //                   <MenuItem value="all">All Regions</MenuItem>
// // //                   <MenuItem value="UAE">UAE</MenuItem>
// // //                   <MenuItem value="USA">USA</MenuItem>
// // //                   <MenuItem value="EU">EU</MenuItem>
// // //                   <MenuItem value="Singapore">Singapore</MenuItem>
// // //                 </Select>
// // //               </FormControl>
// // //               <Button
// // //                 variant="outlined"
// // //                 color="primary"
// // //                 startIcon={<FileDownloadOutlined />}
// // //                 onClick={handleExportReport}
// // //                 sx={{
// // //                   borderRadius: "50px",
// // //                   textTransform: "none",
// // //                   height: "40px",
// // //                 }}
// // //               >
// // //                 Export Report
// // //               </Button>
// // //             </Box>
// // //           </Paper>

// // //           {/* ROW 2: KPI CARDS */}
// // //           <Box display="flex" gap={{ xs: 1.5, md: 2 }}>
// // //             {(loading ? [...Array(5)] : kpiCards).map((item, index) => (
// // //               <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
// // //                 {loading ? (
// // //                   <Skeleton
// // //                     variant="rectangular"
// // //                     width="100%"
// // //                     height={120}
// // //                     sx={{ borderRadius: "14px" }}
// // //                   />
// // //                 ) : (
// // //                   <Card
// // //                     sx={{
// // //                       position: "relative",
// // //                       backgroundColor: theme.palette.primary.main,
// // //                       color: theme.palette.primary.contrastText,
// // //                       height: "120px",
// // //                       borderRadius: "14px",
// // //                       overflow: "hidden",
// // //                       boxShadow: theme.shadows[2],
// // //                     }}
// // //                   >
// // //                     <svg
// // //                       width="80"
// // //                       height="120"
// // //                       viewBox="0 0 80 120"
// // //                       style={{ position: "absolute", right: 0, top: 0 }}
// // //                     >
// // //                       <path
// // //                         d="M20 0 Q60 30 40 60 Q20 90 60 120 L80 120 L80 0 Z"
// // //                         fill={theme.palette.accent.main}
// // //                       />
// // //                     </svg>
// // //                     <CardContent
// // //                       sx={{
// // //                         position: "relative",
// // //                         zIndex: 1,
// // //                         p: { xs: 1.5, md: 2 },
// // //                       }}
// // //                     >
// // //                       <Typography
// // //                         sx={{
// // //                           fontWeight: "bold",
// // //                           fontSize: { xs: "0.7rem", md: "0.8rem" },
// // //                           textTransform: "uppercase",
// // //                           color: alpha(theme.palette.primary.contrastText, 0.8),
// // //                           mb: 1,
// // //                           lineHeight: 1.2,
// // //                         }}
// // //                       >
// // //                         {item.title}
// // //                       </Typography>
// // //                       <Typography
// // //                         sx={{
// // //                           fontWeight: "bold",
// // //                           fontSize: { xs: "1.5rem", md: "1.75rem" },
// // //                           lineHeight: 1,
// // //                         }}
// // //                       >
// // //                         {item.value}
// // //                       </Typography>
// // //                     </CardContent>
// // //                   </Card>
// // //                 )}
// // //               </Box>
// // //             ))}
// // //           </Box>

// // //           {/* ROW 3: TWO COLUMNS (SKILLS & INSTITUTIONS) */}
// // //           <Box
// // //             sx={{
// // //               display: "flex",
// // //               flexDirection: { xs: "column", md: "row" },
// // //               gap: 3,
// // //             }}
// // //           >
// // //             {/* Column 1: Skills in Demand */}
// // //             <Box sx={{ flex: { md: 1 }, minWidth: 0 }}>
// // //               <Paper
// // //                 elevation={0}
// // //                 sx={{
// // //                   ...sectionPaperStyles,
// // //                   height: "100%",
// // //                   display: "flex",
// // //                   flexDirection: "column",
// // //                 }}
// // //               >
// // //                 <Typography
// // //                   variant="h6"
// // //                   sx={{
// // //                     fontWeight: "bold",
// // //                     mb: 2,
// // //                     color: theme.palette.primary.main,
// // //                   }}
// // //                 >
// // //                   Skill In High Demand
// // //                 </Typography>
// // //                 <Box sx={{ maxHeight: 300, overflowY: "auto", pr: 1 }}>
// // //                   <Stack spacing={1.5}>
// // //                     {(loading ? [...Array(5)] : skillDemand).map(
// // //                       (skill, index) => (
// // //                         <Paper
// // //                           key={index}
// // //                           elevation={0}
// // //                           sx={{
// // //                             p: 2,
// // //                             borderRadius: "12px",
// // //                             border: `1px solid ${theme.palette.divider}`,
// // //                             backgroundColor: "background.paper",
// // //                           }}
// // //                         >
// // //                           {loading ? (
// // //                             <Skeleton height={60} />
// // //                           ) : (
// // //                             <>
// // //                               <Box
// // //                                 display="flex"
// // //                                 justifyContent="space-between"
// // //                                 alignItems="center"
// // //                                 mb={1}
// // //                               >
// // //                                 <Typography
// // //                                   variant="body1"
// // //                                   sx={{ fontWeight: "bold" }}
// // //                                 >
// // //                                   {skill.skill}
// // //                                 </Typography>
// // //                                 <Chip
// // //                                   label={`+${skill.growth}%`}
// // //                                   size="small"
// // //                                   sx={{
// // //                                     backgroundColor: alpha(
// // //                                       theme.palette.warning.main,
// // //                                       0.2
// // //                                     ),
// // //                                     color:
// // //                                       theme.palette.warning.dark ||
// // //                                       theme.palette.warning.main,
// // //                                     fontWeight: "bold",
// // //                                   }}
// // //                                 />
// // //                               </Box>
// // //                               <LinearProgress
// // //                                 variant="determinate"
// // //                                 value={skill.demand}
// // //                                 color="success"
// // //                                 sx={{
// // //                                   height: 6,
// // //                                   borderRadius: 3,
// // //                                   mb: 1,
// // //                                   backgroundColor: "divider",
// // //                                 }}
// // //                               />
// // //                               <Typography
// // //                                 variant="body2"
// // //                                 color="text.secondary"
// // //                               >
// // //                                 {skill.placements.toLocaleString()} Placement
// // //                               </Typography>
// // //                             </>
// // //                           )}
// // //                         </Paper>
// // //                       )
// // //                     )}
// // //                   </Stack>
// // //                 </Box>
// // //               </Paper>
// // //             </Box>

// // //             {/* Column 2: Top Performing Institutions */}
// // //             <Box sx={{ flex: { md: 2 }, minWidth: 0 }}>
// // //               <Paper
// // //                 elevation={0}
// // //                 sx={{
// // //                   ...sectionPaperStyles,
// // //                   height: "100%",
// // //                   display: "flex",
// // //                   flexDirection: "column",
// // //                 }}
// // //               >
// // //                 <Typography
// // //                   variant="h6"
// // //                   sx={{
// // //                     fontWeight: "bold",
// // //                     mb: 2,
// // //                     color: theme.palette.primary.main,
// // //                   }}
// // //                 >
// // //                   Top Performing Institutions
// // //                 </Typography>
// // //                 <TableContainer>
// // //                   <Table size="small">
// // //                     <TableBody>
// // //                       {(loading
// // //                         ? [...Array(rowsPerPage)]
// // //                         : institutionData.slice(
// // //                             page * rowsPerPage,
// // //                             page * rowsPerPage + rowsPerPage
// // //                           )
// // //                       ).map((inst, index) => (
// // //                         <TableRow
// // //                           key={index}
// // //                           sx={{ "& td, & th": { border: 0, py: 1.5 } }}
// // //                         >
// // //                           {loading ? (
// // //                             <TableCell colSpan={4}>
// // //                               <Skeleton />
// // //                             </TableCell>
// // //                           ) : (
// // //                             <>
// // //                               <TableCell sx={{ width: "50%" }}>
// // //                                 <Box display="flex" alignItems="center">
// // //                                   <School
// // //                                     sx={{
// // //                                       mr: 1.5,
// // //                                       fontSize: 18,
// // //                                       color: "text.secondary",
// // //                                     }}
// // //                                   />
// // //                                   <Typography variant="body2" fontWeight="bold">
// // //                                     {inst.institution}
// // //                                   </Typography>
// // //                                 </Box>
// // //                               </TableCell>
// // //                               <TableCell align="center">
// // //                                 {inst.graduates}{" "}
// // //                                 <Typography
// // //                                   variant="caption"
// // //                                   color="text.secondary"
// // //                                 >
// // //                                   Grads
// // //                                 </Typography>
// // //                               </TableCell>
// // //                               <TableCell align="center">
// // //                                 {inst.placements}{" "}
// // //                                 <Typography
// // //                                   variant="caption"
// // //                                   color="text.secondary"
// // //                                 >
// // //                                   Hired
// // //                                 </Typography>
// // //                               </TableCell>
// // //                               <TableCell align="right">
// // //                                 <Chip
// // //                                   label={`${inst.rate}%`}
// // //                                   size="small"
// // //                                   sx={{
// // //                                     backgroundColor:
// // //                                       inst.rate >= 90
// // //                                         ? alpha(
// // //                                             theme.palette.success.main,
// // //                                             0.15
// // //                                           )
// // //                                         : alpha(
// // //                                             theme.palette.warning.main,
// // //                                             0.15
// // //                                           ),
// // //                                     color:
// // //                                       inst.rate >= 90
// // //                                         ? theme.palette.success.dark
// // //                                         : theme.palette.warning.dark,
// // //                                     fontWeight: "bold",
// // //                                   }}
// // //                                 />
// // //                               </TableCell>
// // //                             </>
// // //                           )}
// // //                         </TableRow>
// // //                       ))}
// // //                     </TableBody>
// // //                   </Table>
// // //                 </TableContainer>
// // //                 <TablePagination
// // //                   component="div"
// // //                   count={institutionData.length}
// // //                   page={page}
// // //                   onPageChange={handleChangePage}
// // //                   rowsPerPage={rowsPerPage}
// // //                   onRowsPerPageChange={handleChangeRowsPerPage}
// // //                   rowsPerPageOptions={[5, 10, 25]}
// // //                   sx={{ mt: "auto", pt: 1 }}
// // //                 />
// // //               </Paper>
// // //             </Box>
// // //           </Box>

// // //           {/* ROW 4: REGIONAL PERFORMANCE */}
// // //           <Paper
// // //             elevation={0}
// // //             sx={{
// // //               ...sectionPaperStyles,
// // //               display: "flex",
// // //               flexDirection: "column",
// // //             }}
// // //           >
// // //             <Typography
// // //               variant="h6"
// // //               sx={{
// // //                 fontWeight: "bold",
// // //                 mb: 2,
// // //                 color: theme.palette.primary.main,
// // //               }}
// // //             >
// // //               Regional Performance
// // //             </Typography>
// // //             <TableContainer>
// // //               <Table>
// // //                 <TableHead>
// // //                   <TableRow>
// // //                     <TableCell>Region</TableCell>
// // //                     <TableCell>Placement</TableCell>
// // //                     <TableCell>Growth</TableCell>
// // //                     <TableCell>Avg Salary</TableCell>
// // //                     <TableCell>Demand Score</TableCell>
// // //                     <TableCell>Top Roles</TableCell>
// // //                   </TableRow>
// // //                 </TableHead>
// // //                 <TableBody>
// // //                   {(loading
// // //                     ? [...Array(regionalRowsPerPage)]
// // //                     : regionData.slice(
// // //                         regionalPage * regionalRowsPerPage,
// // //                         regionalPage * regionalRowsPerPage + regionalRowsPerPage
// // //                       )
// // //                   ).map((item, index) => (
// // //                     <TableRow
// // //                       key={index}
// // //                       sx={{
// // //                         "&:hover": {
// // //                           backgroundColor: theme.palette.action.hover,
// // //                         },
// // //                       }}
// // //                     >
// // //                       {loading ? (
// // //                         <TableCell colSpan={6}>
// // //                           <Skeleton />
// // //                         </TableCell>
// // //                       ) : (
// // //                         <>
// // //                           <TableCell sx={{ fontWeight: "bold" }}>
// // //                             {item.region}
// // //                           </TableCell>
// // //                           <TableCell
// // //                             sx={{ color: "error.main", fontWeight: 500 }}
// // //                           >
// // //                             {item.placements.toLocaleString()}
// // //                           </TableCell>
// // //                           <TableCell
// // //                             sx={{ color: "success.main", fontWeight: 500 }}
// // //                           >
// // //                             +{item.growth}%
// // //                           </TableCell>
// // //                           <TableCell sx={{ fontWeight: "bold" }}>
// // //                             {item.avgSalary}
// // //                           </TableCell>
// // //                           <TableCell>
// // //                             <Box display="flex" alignItems="center">
// // //                               <LinearProgress
// // //                                 variant="determinate"
// // //                                 value={item.demand_score}
// // //                                 color="success"
// // //                                 sx={{
// // //                                   width: 60,
// // //                                   mr: 1,
// // //                                   backgroundColor: "divider",
// // //                                 }}
// // //                               />
// // //                               <Typography
// // //                                 variant="caption"
// // //                                 sx={{ color: "success.main", fontWeight: 500 }}
// // //                               >
// // //                                 {item.demand_score}%
// // //                               </Typography>
// // //                             </Box>
// // //                           </TableCell>
// // //                           <TableCell>
// // //                             <Typography variant="body2">
// // //                               {(item.top_roles || []).join(", ")}
// // //                             </Typography>
// // //                           </TableCell>
// // //                         </>
// // //                       )}
// // //                     </TableRow>
// // //                   ))}
// // //                 </TableBody>
// // //               </Table>
// // //             </TableContainer>
// // //             <TablePagination
// // //               component="div"
// // //               count={regionData.length}
// // //               page={regionalPage}
// // //               onPageChange={handleChangeRegionalPage}
// // //               rowsPerPage={regionalRowsPerPage}
// // //               onRowsPerPageChange={handleChangeRegionalRowsPerPage}
// // //               rowsPerPageOptions={[5, 10, 25]}
// // //               sx={{ mt: "auto", pt: 1 }}
// // //             />
// // //           </Paper>
// // //         </Stack>
// // //       </Box>
// // //     </Layout>
// // //   );
// // // };

// // // export default Analytics;
// // "use client";

// // import { useState, useEffect } from "react";
// // import {
// //   Card,
// //   CardContent,
// //   Typography,
// //   Box,
// //   FormControl,
// //   Select,
// //   MenuItem,
// //   Button,
// //   Table,
// //   TableBody,
// //   TableCell,
// //   TableContainer,
// //   TableHead,
// //   TableRow,
// //   LinearProgress,
// //   Chip,
// //   Paper,
// //   Skeleton,
// //   Stack,
// //   TablePagination,
// //   Dialog,
// //   DialogTitle,
// //   DialogContent,
// //   DialogActions,
// //   IconButton,
// // } from "@mui/material";
// // import { useTheme, alpha } from "@mui/material/styles";
// // import {
// //   School,
// //   AccessTime,
// //   Public,
// //   FileDownloadOutlined,
// //   KeyboardArrowDown,
// //   Close as CloseIcon,
// // } from "@mui/icons-material";
// // import Layout from "../common/Layout";
// // import { useNotification } from "../../context/NotificationContext";
// // import apiClient from "../../context/apiClient";

// // const Analytics = () => {
// //   const theme = useTheme();
// //   const { showNotification } = useNotification();
// //   const [timeRange, setTimeRange] = useState("month");
// //   const [region, setRegion] = useState("all");
// //   const [loading, setLoading] = useState(true);

// //   // --- STATE FOR REGIONAL PERFORMANCE PAGINATION ---
// //   const [regionalPage, setRegionalPage] = useState(0);
// //   const [regionalRowsPerPage, setRegionalRowsPerPage] = useState(5);

// //   // --- STATE FOR INSTITUTIONS DIALOG ---
// //   const [isDialogOpen, setIsDialogOpen] = useState(false);

// //   const [globalMetrics, setGlobalMetrics] = useState({
// //     totalTalent: 0,
// //     activePlacements: 0,
// //     totalEmployers: 0,
// //     successRate: 0,
// //     avgPlacementTime: 0,
// //   });
// //   const [regionData, setRegionData] = useState([]);
// //   const [skillDemand, setSkillDemand] = useState([]);
// //   const [institutionData, setInstitutionData] = useState([]);

// //   // --- HANDLERS FOR REGIONAL PAGINATION ---
// //   const handleRegionalChangePage = (event, newPage) => {
// //     setRegionalPage(newPage);
// //   };

// //   const handleRegionalChangeRowsPerPage = (event) => {
// //     setRegionalRowsPerPage(parseInt(event.target.value, 10));
// //     setRegionalPage(0);
// //   };

// //   // --- HANDLERS FOR DIALOG ---
// //   const handleOpenDialog = () => setIsDialogOpen(true);
// //   const handleCloseDialog = () => setIsDialogOpen(false);

// //   useEffect(() => {
// //     const fetchDashboardData = async () => {
// //       setLoading(true);
// //       try {
// //         const response = await apiClient.get("/dashboard/global-overview/");
// //         const data = response.data;

// //         setGlobalMetrics({
// //           totalTalent: data.global_ai_talent || 0,
// //           activePlacements: data.active_placements || 0,
// //           totalEmployers: data.global_employer || 0,
// //           successRate: Number.parseFloat(data.success_rate || 0).toFixed(2),
// //           avgPlacementTime: Number.parseFloat(
// //             data.average_days_to_place || 0
// //           ).toFixed(0),
// //         });

// //         const topRolesExamples = [
// //           ["AI Engineer"],
// //           ["Java Developer"],
// //           ["Python", "HTML"],
// //           ["UIUX Designer"],
// //         ];

// //         setRegionData(
// //           (data.placements_by_region || []).map((item, index) => ({
// //             ...item,
// //             avgSalary: `$${Number(item.avg_salary || 0).toLocaleString()}`,
// //             top_roles:
// //               item.top_roles ||
// //               topRolesExamples[index % topRolesExamples.length],
// //           }))
// //         );

// //         const skills = data.skills_in_high_demand || [];
// //         const maxPlacements = Math.max(...skills.map((s) => s.placements), 0);
// //         setSkillDemand(
// //           skills.map((item) => ({
// //             ...item,
// //             demand:
// //               maxPlacements > 0 ? (item.placements / maxPlacements) * 100 : 0,
// //           }))
// //         );

// //         setInstitutionData(
// //           (data.top_performing_institutions || []).map((item) => ({
// //             institution: item.institution_name || "Unknown Institution",
// //             graduates: item.graduates || 0,
// //             placements: item.placements || 0,
// //             rate: Number.parseFloat(item.success_rate || 0).toFixed(0),
// //           }))
// //         );
// //       } catch (error) {
// //         console.error("Failed to fetch dashboard data:", error);
// //         showNotification(
// //           "Failed to load dashboard analytics. Please try again later.",
// //           "error"
// //         );
// //       } finally {
// //         setLoading(false);
// //       }
// //     };

// //     fetchDashboardData();
// //   }, [showNotification]);

// //   const handleExportReport = () => {
// //     showNotification("Analytics report exported successfully!", "success");
// //   };

// //   const kpiCards = [
// //     { title: "TOTAL AI TALENT", value: globalMetrics.totalTalent },
// //     { title: "ACTIVE PLACEMENT", value: globalMetrics.activePlacements },
// //     { title: "GLOBAL EMPLOYERS", value: globalMetrics.totalEmployers },
// //     { title: "SUCCESS RATE", value: `${globalMetrics.successRate}%` },
// //     { title: "AVG DAYS TO PLACE", value: globalMetrics.avgPlacementTime },
// //   ];

// //   const sectionPaperStyles = {
// //     p: 3,
// //     borderRadius: "20px",
// //     boxShadow: theme.shadows[1],
// //     backgroundColor: theme.palette.mint.main,
// //   };

// //   return (
// //     <Layout title="Analytics Dashboard">
// //       <Box
// //         sx={{
// //           p: { xs: 2, md: 3 },
// //           backgroundColor: theme.palette.background.default,
// //         }}
// //       >
// //         <Stack direction="column" spacing={3}>
// //           {/* ROW 1: HEADER PANEL */}
// //           <Paper
// //             elevation={0}
// //             sx={{
// //               ...sectionPaperStyles,
// //               display: "flex",
// //               justifyContent: "space-between",
// //               alignItems: { xs: "flex-start", md: "center" },
// //               flexDirection: { xs: "column", md: "row" },
// //             }}
// //           >
// //             <Box mb={{ xs: 3, md: 0 }}>
// //               <Typography
// //                 variant="h4"
// //                 sx={{ fontWeight: "bold", color: theme.palette.primary.main }}
// //               >
// //                 Analytics
// //               </Typography>
// //               <Typography variant="body1" color="text.secondary">
// //                 Comprehensive insight into AI Talent export Performance
// //               </Typography>
// //             </Box>
// //             <Box
// //               display="flex"
// //               gap={2}
// //               flexWrap="wrap"
// //               justifyContent={{ xs: "flex-start", md: "flex-end" }}
// //             >
// //               <FormControl sx={{ minWidth: 160 }}>
// //                 <Select
// //                   value={timeRange}
// //                   onChange={(e) => setTimeRange(e.target.value)}
// //                   IconComponent={KeyboardArrowDown}
// //                   size="small"
// //                   sx={{
// //                     borderRadius: "50px",
// //                     color: "primary.main",
// //                     "& .MuiOutlinedInput-notchedOutline": {
// //                       borderColor: "primary.main",
// //                     },
// //                     "& .MuiSvgIcon-root": { color: "primary.main" },
// //                   }}
// //                   renderValue={(value) => (
// //                     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
// //                       <AccessTime fontSize="small" />
// //                       <Typography variant="body2">
// //                         {value === "week"
// //                           ? "Last Week"
// //                           : value === "month"
// //                           ? "Last Month"
// //                           : value === "quarter"
// //                           ? "Last Quarter"
// //                           : "Last Year"}
// //                       </Typography>
// //                     </Box>
// //                   )}
// //                 >
// //                   <MenuItem value="week">Last Week</MenuItem>
// //                   <MenuItem value="month">Last Month</MenuItem>
// //                   <MenuItem value="quarter">Last Quarter</MenuItem>
// //                   <MenuItem value="year">Last Year</MenuItem>
// //                 </Select>
// //               </FormControl>
// //               <FormControl sx={{ minWidth: 160 }}>
// //                 <Select
// //                   value={region}
// //                   onChange={(e) => setRegion(e.target.value)}
// //                   IconComponent={KeyboardArrowDown}
// //                   size="small"
// //                   sx={{
// //                     borderRadius: "50px",
// //                     color: "primary.main",
// //                     "& .MuiOutlinedInput-notchedOutline": {
// //                       borderColor: "primary.main",
// //                     },
// //                     "& .MuiSvgIcon-root": { color: "primary.main" },
// //                   }}
// //                   renderValue={(value) => (
// //                     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
// //                       <Public fontSize="small" />
// //                       <Typography variant="body2">
// //                         {value === "all" ? "All Regions" : value}
// //                       </Typography>
// //                     </Box>
// //                   )}
// //                 >
// //                   <MenuItem value="all">All Regions</MenuItem>
// //                   <MenuItem value="UAE">UAE</MenuItem>
// //                   <MenuItem value="USA">USA</MenuItem>
// //                   <MenuItem value="EU">EU</MenuItem>
// //                   <MenuItem value="Singapore">Singapore</MenuItem>
// //                 </Select>
// //               </FormControl>
// //               <Button
// //                 variant="outlined"
// //                 color="primary"
// //                 startIcon={<FileDownloadOutlined />}
// //                 onClick={handleExportReport}
// //                 sx={{
// //                   borderRadius: "50px",
// //                   textTransform: "none",
// //                   height: "40px",
// //                 }}
// //               >
// //                 Export Report
// //               </Button>
// //             </Box>
// //           </Paper>

// //           {/* ROW 2: KPI CARDS */}
// //           <Box display="flex" gap={{ xs: 1.5, md: 2 }}>
// //             {(loading ? [...Array(5)] : kpiCards).map((item, index) => (
// //               <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
// //                 {loading ? (
// //                   <Skeleton
// //                     variant="rectangular"
// //                     width="100%"
// //                     height={120}
// //                     sx={{ borderRadius: "14px" }}
// //                   />
// //                 ) : (
// //                   <Card
// //                     sx={{
// //                       position: "relative",
// //                       backgroundColor: theme.palette.primary.main,
// //                       color: theme.palette.primary.contrastText,
// //                       height: "120px",
// //                       borderRadius: "14px",
// //                       overflow: "hidden",
// //                       boxShadow: theme.shadows[2],
// //                     }}
// //                   >
// //                     <svg
// //                       width="80"
// //                       height="120"
// //                       viewBox="0 0 80 120"
// //                       style={{ position: "absolute", right: 0, top: 0 }}
// //                     >
// //                       <path
// //                         d="M20 0 Q60 30 40 60 Q20 90 60 120 L80 120 L80 0 Z"
// //                         fill={theme.palette.accent.main}
// //                       />
// //                     </svg>
// //                     <CardContent
// //                       sx={{
// //                         position: "relative",
// //                         zIndex: 1,
// //                         p: { xs: 1.5, md: 2 },
// //                       }}
// //                     >
// //                       <Typography
// //                         sx={{
// //                           fontWeight: "bold",
// //                           fontSize: { xs: "0.7rem", md: "0.8rem" },
// //                           textTransform: "uppercase",
// //                           color: alpha(theme.palette.primary.contrastText, 0.8),
// //                           mb: 1,
// //                           lineHeight: 1.2,
// //                         }}
// //                       >
// //                         {item.title}
// //                       </Typography>
// //                       <Typography
// //                         sx={{
// //                           fontWeight: "bold",
// //                           fontSize: { xs: "1.5rem", md: "1.75rem" },
// //                           lineHeight: 1,
// //                         }}
// //                       >
// //                         {item.value}
// //                       </Typography>
// //                     </CardContent>
// //                   </Card>
// //                 )}
// //               </Box>
// //             ))}
// //           </Box>

// //           {/* ROW 3: TWO COLUMNS (SKILLS & INSTITUTIONS) */}
// //           <Box
// //             sx={{
// //               display: "flex",
// //               flexDirection: { xs: "column", md: "row" },
// //               gap: 3,
// //             }}
// //           >
// //             {/* Column 1: Skills in Demand */}
// //             <Box sx={{ flex: { md: 1 }, minWidth: 0 }}>
// //               <Paper
// //                 elevation={0}
// //                 sx={{
// //                   ...sectionPaperStyles,
// //                   height: "100%",
// //                   display: "flex",
// //                   flexDirection: "column",
// //                 }}
// //               >
// //                 <Typography
// //                   variant="h6"
// //                   sx={{
// //                     fontWeight: "bold",
// //                     mb: 2,
// //                     color: theme.palette.primary.main,
// //                   }}
// //                 >
// //                   Skill In High Demand
// //                 </Typography>
// //                 <Box sx={{ maxHeight: 300, overflowY: "auto", pr: 1 }}>
// //                   <Stack spacing={1.5}>
// //                     {(loading ? [...Array(5)] : skillDemand).map(
// //                       (skill, index) => (
// //                         <Paper
// //                           key={index}
// //                           elevation={0}
// //                           sx={{
// //                             p: 2,
// //                             borderRadius: "12px",
// //                             border: `1px solid ${theme.palette.divider}`,
// //                             backgroundColor: "background.paper",
// //                           }}
// //                         >
// //                           {loading ? (
// //                             <Skeleton height={60} />
// //                           ) : (
// //                             <>
// //                               <Box
// //                                 display="flex"
// //                                 justifyContent="space-between"
// //                                 alignItems="center"
// //                                 mb={1}
// //                               >
// //                                 <Typography
// //                                   variant="body1"
// //                                   sx={{ fontWeight: "bold" }}
// //                                 >
// //                                   {skill.skill}
// //                                 </Typography>
// //                                 <Chip
// //                                   label={`+${skill.growth}%`}
// //                                   size="small"
// //                                   sx={{
// //                                     backgroundColor: alpha(
// //                                       theme.palette.warning.main,
// //                                       0.2
// //                                     ),
// //                                     color:
// //                                       theme.palette.warning.dark ||
// //                                       theme.palette.warning.main,
// //                                     fontWeight: "bold",
// //                                   }}
// //                                 />
// //                               </Box>
// //                               <LinearProgress
// //                                 variant="determinate"
// //                                 value={skill.demand}
// //                                 color="success"
// //                                 sx={{
// //                                   height: 6,
// //                                   borderRadius: 3,
// //                                   mb: 1,
// //                                   backgroundColor: "divider",
// //                                 }}
// //                               />
// //                               <Typography
// //                                 variant="body2"
// //                                 color="text.secondary"
// //                               >
// //                                 {skill.placements.toLocaleString()} Placement
// //                               </Typography>
// //                             </>
// //                           )}
// //                         </Paper>
// //                       )
// //                     )}
// //                   </Stack>
// //                 </Box>
// //               </Paper>
// //             </Box>

// //             {/* Column 2: Top Performing Institutions */}
// //             <Box sx={{ flex: { md: 2 }, minWidth: 0 }}>
// //               <Paper
// //                 elevation={0}
// //                 sx={{
// //                   ...sectionPaperStyles,
// //                   height: "100%",
// //                   display: "flex",
// //                   flexDirection: "column",
// //                 }}
// //               >
// //                 <Typography
// //                   variant="h6"
// //                   sx={{
// //                     fontWeight: "bold",
// //                     mb: 2,
// //                     color: theme.palette.primary.main,
// //                   }}
// //                 >
// //                   Top Performing Institutions
// //                 </Typography>
// //                 <TableContainer sx={{ flexGrow: 1 }}>
// //                   <Table size="small">
// //                     <TableBody>
// //                       {(loading
// //                         ? [...Array(5)]
// //                         : institutionData.slice(0, 5)
// //                       ).map((inst, index) => (
// //                         <TableRow
// //                           key={index}
// //                           sx={{ "& td, & th": { border: 0, py: 1.5 } }}
// //                         >
// //                           {loading ? (
// //                             <TableCell colSpan={4}>
// //                               <Skeleton />
// //                             </TableCell>
// //                           ) : (
// //                             <>
// //                               <TableCell sx={{ width: "50%" }}>
// //                                 <Box display="flex" alignItems="center">
// //                                   <School
// //                                     sx={{
// //                                       mr: 1.5,
// //                                       fontSize: 18,
// //                                       color: "text.secondary",
// //                                     }}
// //                                   />
// //                                   <Typography variant="body2" fontWeight="bold">
// //                                     {inst.institution}
// //                                   </Typography>
// //                                 </Box>
// //                               </TableCell>
// //                               <TableCell align="center">
// //                                 {inst.graduates}{" "}
// //                                 <Typography
// //                                   variant="caption"
// //                                   color="text.secondary"
// //                                 >
// //                                   Grads
// //                                 </Typography>
// //                               </TableCell>
// //                               <TableCell align="center">
// //                                 {inst.placements}{" "}
// //                                 <Typography
// //                                   variant="caption"
// //                                   color="text.secondary"
// //                                 >
// //                                   Hired
// //                                 </Typography>
// //                               </TableCell>
// //                               <TableCell align="right">
// //                                 <Chip
// //                                   label={`${inst.rate}%`}
// //                                   size="small"
// //                                   sx={{
// //                                     backgroundColor:
// //                                       inst.rate >= 90
// //                                         ? alpha(
// //                                             theme.palette.success.main,
// //                                             0.15
// //                                           )
// //                                         : alpha(
// //                                             theme.palette.warning.main,
// //                                             0.15
// //                                           ),
// //                                     color:
// //                                       inst.rate >= 90
// //                                         ? theme.palette.success.dark
// //                                         : theme.palette.warning.dark,
// //                                     fontWeight: "bold",
// //                                   }}
// //                                 />
// //                               </TableCell>
// //                             </>
// //                           )}
// //                         </TableRow>
// //                       ))}
// //                     </TableBody>
// //                   </Table>
// //                 </TableContainer>
// //                 <Box
// //                   sx={{ display: "flex", justifyContent: "flex-end", pt: 1 }}
// //                 >
// //                   <Button size="small" onClick={handleOpenDialog}>
// //                     See All
// //                   </Button>
// //                 </Box>
// //               </Paper>
// //             </Box>
// //           </Box>

// //           {/* ROW 4: REGIONAL PERFORMANCE */}
// //           <Paper
// //             elevation={0}
// //             sx={{
// //               ...sectionPaperStyles,
// //               display: "flex",
// //               flexDirection: "column",
// //             }}
// //           >
// //             <Typography
// //               variant="h6"
// //               sx={{
// //                 fontWeight: "bold",
// //                 mb: 2,
// //                 color: theme.palette.primary.main,
// //               }}
// //             >
// //               Regional Performance
// //             </Typography>
// //             <TableContainer>
// //               <Table>
// //                 <TableHead>
// //                   <TableRow>
// //                     <TableCell>Region</TableCell>
// //                     <TableCell>Placement</TableCell>
// //                     <TableCell>Growth</TableCell>
// //                     <TableCell>Avg Salary</TableCell>
// //                     <TableCell>Demand Score</TableCell>
// //                     <TableCell>Top Roles</TableCell>
// //                   </TableRow>
// //                 </TableHead>
// //                 <TableBody>
// //                   {(loading
// //                     ? [...Array(regionalRowsPerPage)]
// //                     : regionData.slice(
// //                         regionalPage * regionalRowsPerPage,
// //                         regionalPage * regionalRowsPerPage + regionalRowsPerPage
// //                       )
// //                   ).map((item, index) => (
// //                     <TableRow
// //                       key={index}
// //                       sx={{
// //                         "&:hover": {
// //                           backgroundColor: theme.palette.action.hover,
// //                         },
// //                       }}
// //                     >
// //                       {loading ? (
// //                         <TableCell colSpan={6}>
// //                           <Skeleton />
// //                         </TableCell>
// //                       ) : (
// //                         <>
// //                           <TableCell sx={{ fontWeight: "bold" }}>
// //                             {item.region}
// //                           </TableCell>
// //                           <TableCell
// //                             sx={{ color: "error.main", fontWeight: 500 }}
// //                           >
// //                             {item.placements.toLocaleString()}
// //                           </TableCell>
// //                           <TableCell
// //                             sx={{ color: "success.main", fontWeight: 500 }}
// //                           >
// //                             +{item.growth}%
// //                           </TableCell>
// //                           <TableCell sx={{ fontWeight: "bold" }}>
// //                             {item.avgSalary}
// //                           </TableCell>
// //                           <TableCell>
// //                             <Box display="flex" alignItems="center">
// //                               <LinearProgress
// //                                 variant="determinate"
// //                                 value={item.demand_score}
// //                                 color="success"
// //                                 sx={{
// //                                   width: 60,
// //                                   mr: 1,
// //                                   backgroundColor: "divider",
// //                                 }}
// //                               />
// //                               <Typography
// //                                 variant="caption"
// //                                 sx={{ color: "success.main", fontWeight: 500 }}
// //                               >
// //                                 {item.demand_score}%
// //                               </Typography>
// //                             </Box>
// //                           </TableCell>
// //                           <TableCell>
// //                             <Typography variant="body2">
// //                               {(item.top_roles || []).join(", ")}
// //                             </Typography>
// //                           </TableCell>
// //                         </>
// //                       )}
// //                     </TableRow>
// //                   ))}
// //                 </TableBody>
// //               </Table>
// //             </TableContainer>
// //             <TablePagination
// //               component="div"
// //               count={regionData.length}
// //               page={regionalPage}
// //               onPageChange={handleRegionalChangePage}
// //               rowsPerPage={regionalRowsPerPage}
// //               onRowsPerPageChange={handleRegionalChangeRowsPerPage}
// //               rowsPerPageOptions={[5, 10, 25]}
// //               sx={{ mt: "auto", pt: 1 }}
// //             />
// //           </Paper>
// //         </Stack>
// //       </Box>

// //       {/* DIALOG FOR ALL INSTITUTIONS */}
// //       <Dialog
// //         open={isDialogOpen}
// //         onClose={handleCloseDialog}
// //         fullWidth
// //         maxWidth="md"
// //       >
// //         <DialogTitle>
// //           All Performing Institutions
// //           <IconButton
// //             onClick={handleCloseDialog}
// //             sx={{ position: "absolute", right: 8, top: 8 }}
// //           >
// //             <CloseIcon />
// //           </IconButton>
// //         </DialogTitle>
// //         <DialogContent dividers>
// //           <TableContainer>
// //             <Table stickyHeader size="small">
// //               <TableHead>
// //                 <TableRow>
// //                   <TableCell sx={{ width: "50%" }}>Institution</TableCell>
// //                   <TableCell align="center">Graduates</TableCell>
// //                   <TableCell align="center">Placements</TableCell>
// //                   <TableCell align="right">Success Rate</TableCell>
// //                 </TableRow>
// //               </TableHead>
// //               <TableBody>
// //                 {institutionData.map((inst, index) => (
// //                   <TableRow
// //                     key={index}
// //                     sx={{ "& td, & th": { border: 0, py: 1.5 } }}
// //                   >
// //                     <TableCell>
// //                       <Box display="flex" alignItems="center">
// //                         <School
// //                           sx={{
// //                             mr: 1.5,
// //                             fontSize: 18,
// //                             color: "text.secondary",
// //                           }}
// //                         />
// //                         <Typography variant="body2" fontWeight="bold">
// //                           {inst.institution}
// //                         </Typography>
// //                       </Box>
// //                     </TableCell>
// //                     <TableCell align="center">{inst.graduates}</TableCell>
// //                     <TableCell align="center">{inst.placements}</TableCell>
// //                     <TableCell align="right">
// //                       <Chip
// //                         label={`${inst.rate}%`}
// //                         size="small"
// //                         sx={{
// //                           backgroundColor:
// //                             inst.rate >= 90
// //                               ? alpha(theme.palette.success.main, 0.15)
// //                               : alpha(theme.palette.warning.main, 0.15),
// //                           color:
// //                             inst.rate >= 90
// //                               ? theme.palette.success.dark
// //                               : theme.palette.warning.dark,
// //                           fontWeight: "bold",
// //                         }}
// //                       />
// //                     </TableCell>
// //                   </TableRow>
// //                 ))}
// //               </TableBody>
// //             </Table>
// //           </TableContainer>
// //         </DialogContent>
// //         <DialogActions>
// //           <Button onClick={handleCloseDialog}>Close</Button>
// //         </DialogActions>
// //       </Dialog>
// //     </Layout>
// //   );
// // };

// // export default Analytics;
// "use client";

// import { useState, useEffect, useMemo } from "react";
// import {
//   Grid,
//   Card,
//   CardContent,
//   Typography,
//   Box,
//   FormControl,
//   Select,
//   MenuItem,
//   Button,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   LinearProgress,
//   Chip,
//   Paper,
//   Skeleton,
//   Stack,
//   TablePagination,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   TextField,
//   IconButton,
// } from "@mui/material";
// import { useTheme, alpha } from "@mui/material/styles";
// import {
//   School,
//   AccessTime,
//   Public,
//   FileDownloadOutlined,
//   KeyboardArrowDown,
//   Close as CloseIcon,
// } from "@mui/icons-material";
// import Layout from "../common/Layout";
// import { useNotification } from "../../context/NotificationContext";
// import apiClient from "../../context/apiClient";

// const Analytics = () => {
//   const theme = useTheme();
//   const { showNotification } = useNotification();
//   const [timeRange, setTimeRange] = useState("month");
//   const [region, setRegion] = useState("all");
//   const [loading, setLoading] = useState(true);

//   // --- STATE FOR DIALOG & SEARCH ---
//   const [dialogOpen, setDialogOpen] = useState(false);
//   const [searchTerm, setSearchTerm] = useState("");

//   // --- STATE FOR REGIONAL PERFORMANCE PAGINATION ---
//   const [regionalPage, setRegionalPage] = useState(0);
//   const [regionalRowsPerPage, setRegionalRowsPerPage] = useState(5);

//   const [globalMetrics, setGlobalMetrics] = useState({
//     totalTalent: 0,
//     activePlacements: 0,
//     totalEmployers: 0,
//     successRate: 0,
//     avgPlacementTime: 0,
//   });
//   const [regionData, setRegionData] = useState([]);
//   const [skillDemand, setSkillDemand] = useState([]);
//   const [institutionData, setInstitutionData] = useState([]);

//   // --- HANDLERS FOR REGIONAL PAGINATION ---
//   const handleRegionalChangePage = (event, newPage) => {
//     setRegionalPage(newPage);
//   };
//   const handleRegionalChangeRowsPerPage = (event) => {
//     setRegionalRowsPerPage(parseInt(event.target.value, 10));
//     setRegionalPage(0);
//   };

//   // --- HANDLERS FOR DIALOG ---
//   const handleOpenDialog = () => setDialogOpen(true);
//   const handleCloseDialog = () => setDialogOpen(false);

//   // Memoize the filtered list for the dialog search
//   const filteredInstitutions = useMemo(() => {
//     if (!searchTerm) {
//       return institutionData;
//     }
//     return institutionData.filter((inst) =>
//       inst.institution.toLowerCase().includes(searchTerm.toLowerCase())
//     );
//   }, [searchTerm, institutionData]);

//   useEffect(() => {
//     const fetchDashboardData = async () => {
//       setLoading(true);
//       try {
//         const response = await apiClient.get("/dashboard/global-overview/");
//         const data = response.data;
//         setGlobalMetrics({
//           totalTalent: data.global_ai_talent || 0,
//           activePlacements: data.active_placements || 0,
//           totalEmployers: data.global_employer || 0,
//           successRate: Number.parseFloat(data.success_rate || 0).toFixed(2),
//           avgPlacementTime: Number.parseFloat(
//             data.average_days_to_place || 0
//           ).toFixed(0),
//         });
//         const topRolesExamples = [
//           ["AI Engineer"],
//           ["Java Developer"],
//           ["Python", "HTML"],
//         ];
//         setRegionData(
//           (data.placements_by_region || []).map((item, index) => ({
//             ...item,
//             avgSalary: `$${Number(item.avg_salary || 0).toLocaleString()}`,
//             top_roles:
//               item.top_roles ||
//               topRolesExamples[index % topRolesExamples.length],
//           }))
//         );
//         const skills = data.skills_in_high_demand || [];
//         const maxPlacements = Math.max(...skills.map((s) => s.placements), 0);
//         setSkillDemand(
//           skills.map((item) => ({
//             ...item,
//             demand:
//               maxPlacements > 0 ? (item.placements / maxPlacements) * 100 : 0,
//           }))
//         );
//         setInstitutionData(
//           (data.top_performing_institutions || []).map((item) => ({
//             institution: item.institution_name || "Unknown Institution",
//             graduates: item.graduates || 0,
//             placements: item.placements || 0,
//             rate: Number.parseFloat(item.success_rate || 0).toFixed(0),
//           }))
//         );
//       } catch (error) {
//         console.error("Failed to fetch dashboard data:", error);
//         showNotification(
//           "Failed to load dashboard analytics. Please try again later.",
//           "error"
//         );
//       } finally {
//         setLoading(false);
//       }
//     };
//     fetchDashboardData();
//   }, [showNotification]);

//   const handleExportReport = () => {
//     showNotification("Analytics report exported successfully!", "success");
//   };

//   const kpiCards = [
//     { title: "TOTAL AI TALENT", value: globalMetrics.totalTalent },
//     { title: "ACTIVE PLACEMENT", value: globalMetrics.activePlacements },
//     { title: "GLOBAL EMPLOYERS", value: globalMetrics.totalEmployers },
//     { title: "SUCCESS RATE", value: `${globalMetrics.successRate}%` },
//     { title: "AVG DAYS TO PLACE", value: globalMetrics.avgPlacementTime },
//   ];

//   const sectionPaperStyles = {
//     p: 3,
//     borderRadius: "20px",
//     boxShadow: theme.shadows[1],
//     backgroundColor: theme.palette.mint.main,
//   };

//   return (
//     <Layout title="Analytics Dashboard">
//       <Box
//         sx={{
//           p: { xs: 2, md: 3 },
//           backgroundColor: theme.palette.background.default,
//         }}
//       >
//         <Stack direction="column" spacing={3}>
//           {/* ROW 1: HEADER PANEL */}
//           <Paper
//             elevation={0}
//             sx={{
//               ...sectionPaperStyles,
//               display: "flex",
//               justifyContent: "space-between",
//               alignItems: { xs: "flex-start", md: "center" },
//               flexDirection: { xs: "column", md: "row" },
//             }}
//           >
//             <Box mb={{ xs: 3, md: 0 }}>
//               <Typography
//                 variant="h4"
//                 sx={{ fontWeight: "bold", color: theme.palette.primary.main }}
//               >
//                 Analytics
//               </Typography>
//               <Typography variant="body1" color="text.secondary">
//                 Comprehensive insight into AI Talent export Performance
//               </Typography>
//             </Box>
//             <Box
//               display="flex"
//               gap={2}
//               flexWrap="wrap"
//               justifyContent={{ xs: "flex-start", md: "flex-end" }}
//             >
//               <FormControl sx={{ minWidth: 160 }}>
//                 <Select
//                   value={timeRange}
//                   onChange={(e) => setTimeRange(e.target.value)}
//                   IconComponent={KeyboardArrowDown}
//                   size="small"
//                   sx={{
//                     borderRadius: "50px",
//                     color: "primary.main",
//                     "& .MuiOutlinedInput-notchedOutline": {
//                       borderColor: "primary.main",
//                     },
//                     "& .MuiSvgIcon-root": { color: "primary.main" },
//                   }}
//                   renderValue={(value) => (
//                     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                       <AccessTime fontSize="small" />
//                       <Typography variant="body2">
//                         {value === "week"
//                           ? "Last Week"
//                           : value === "month"
//                           ? "Last Month"
//                           : value === "quarter"
//                           ? "Last Quarter"
//                           : "Last Year"}
//                       </Typography>
//                     </Box>
//                   )}
//                 >
//                   <MenuItem value="week">Last Week</MenuItem>
//                   <MenuItem value="month">Last Month</MenuItem>
//                   <MenuItem value="quarter">Last Quarter</MenuItem>
//                   <MenuItem value="year">Last Year</MenuItem>
//                 </Select>
//               </FormControl>
//               <FormControl sx={{ minWidth: 160 }}>
//                 <Select
//                   value={region}
//                   onChange={(e) => setRegion(e.target.value)}
//                   IconComponent={KeyboardArrowDown}
//                   size="small"
//                   sx={{
//                     borderRadius: "50px",
//                     color: "primary.main",
//                     "& .MuiOutlinedInput-notchedOutline": {
//                       borderColor: "primary.main",
//                     },
//                     "& .MuiSvgIcon-root": { color: "primary.main" },
//                   }}
//                   renderValue={(value) => (
//                     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                       <Public fontSize="small" />
//                       <Typography variant="body2">
//                         {value === "all" ? "All Regions" : value}
//                       </Typography>
//                     </Box>
//                   )}
//                 >
//                   <MenuItem value="all">All Regions</MenuItem>
//                   <MenuItem value="UAE">UAE</MenuItem>
//                   <MenuItem value="USA">USA</MenuItem>
//                   <MenuItem value="EU">EU</MenuItem>
//                   <MenuItem value="Singapore">Singapore</MenuItem>
//                 </Select>
//               </FormControl>
//               <Button
//                 variant="outlined"
//                 color="primary"
//                 startIcon={<FileDownloadOutlined />}
//                 onClick={handleExportReport}
//                 sx={{
//                   borderRadius: "50px",
//                   textTransform: "none",
//                   height: "40px",
//                 }}
//               >
//                 Export Report
//               </Button>
//             </Box>
//           </Paper>

//           {/* ROW 2: KPI CARDS */}
//           <Box display="flex" gap={{ xs: 1.5, md: 2 }}>
//             {(loading ? [...Array(5)] : kpiCards).map((item, index) => (
//               <Box key={index} sx={{ flex: 1, minWidth: 0 }}>
//                 {loading ? (
//                   <Skeleton
//                     variant="rectangular"
//                     width="100%"
//                     height={120}
//                     sx={{ borderRadius: "14px" }}
//                   />
//                 ) : (
//                   <Card
//                     sx={{
//                       position: "relative",
//                       backgroundColor: theme.palette.primary.main,
//                       color: theme.palette.primary.contrastText,
//                       height: "120px",
//                       borderRadius: "14px",
//                       overflow: "hidden",
//                       boxShadow: theme.shadows[2],
//                     }}
//                   >
//                     <svg
//                       width="80"
//                       height="120"
//                       viewBox="0 0 80 120"
//                       style={{ position: "absolute", right: 0, top: 0 }}
//                     >
//                       <path
//                         d="M20 0 Q60 30 40 60 Q20 90 60 120 L80 120 L80 0 Z"
//                         fill={theme.palette.accent.main}
//                       />
//                     </svg>
//                     <CardContent
//                       sx={{
//                         position: "relative",
//                         zIndex: 1,
//                         p: { xs: 1.5, md: 2 },
//                       }}
//                     >
//                       <Typography
//                         sx={{
//                           fontWeight: "bold",
//                           fontSize: { xs: "0.7rem", md: "0.8rem" },
//                           textTransform: "uppercase",
//                           color: alpha(theme.palette.primary.contrastText, 0.8),
//                           mb: 1,
//                           lineHeight: 1.2,
//                         }}
//                       >
//                         {item.title}
//                       </Typography>
//                       <Typography
//                         sx={{
//                           fontWeight: "bold",
//                           fontSize: { xs: "1.5rem", md: "1.75rem" },
//                           lineHeight: 1,
//                         }}
//                       >
//                         {item.value}
//                       </Typography>
//                     </CardContent>
//                   </Card>
//                 )}
//               </Box>
//             ))}
//           </Box>

//           {/* ROW 3: TWO COLUMNS (SKILLS & INSTITUTIONS) */}
//           <Box
//             sx={{
//               display: "flex",
//               flexDirection: { xs: "column", md: "row" },
//               gap: 3,
//             }}
//           >
//             <Box sx={{ flex: { md: 1 }, minWidth: 0 }}>
//               <Paper
//                 elevation={0}
//                 sx={{
//                   ...sectionPaperStyles,
//                   height: "100%",
//                   display: "flex",
//                   flexDirection: "column",
//                 }}
//               >
//                 <Typography
//                   variant="h6"
//                   sx={{
//                     fontWeight: "bold",
//                     mb: 2,
//                     color: theme.palette.primary.main,
//                   }}
//                 >
//                   Skill In High Demand
//                 </Typography>
//                 <Box sx={{ maxHeight: 300, overflowY: "auto", pr: 1 }}>
//                   <Stack spacing={1.5}>
//                     {(loading ? [...Array(5)] : skillDemand).map(
//                       (skill, index) => (
//                         <Paper
//                           key={index}
//                           elevation={0}
//                           sx={{
//                             p: 2,
//                             borderRadius: "12px",
//                             border: `1px solid ${theme.palette.divider}`,
//                             backgroundColor: "background.paper",
//                           }}
//                         >
//                           {loading ? (
//                             <Skeleton height={60} />
//                           ) : (
//                             <>
//                               <Box
//                                 display="flex"
//                                 justifyContent="space-between"
//                                 alignItems="center"
//                                 mb={1}
//                               >
//                                 <Typography
//                                   variant="body1"
//                                   sx={{ fontWeight: "bold" }}
//                                 >
//                                   {skill.skill}
//                                 </Typography>
//                                 <Chip
//                                   label={`+${skill.growth}%`}
//                                   size="small"
//                                   sx={{
//                                     backgroundColor: alpha(
//                                       theme.palette.warning.main,
//                                       0.2
//                                     ),
//                                     color:
//                                       theme.palette.warning.dark ||
//                                       theme.palette.warning.main,
//                                     fontWeight: "bold",
//                                   }}
//                                 />
//                               </Box>
//                               <LinearProgress
//                                 variant="determinate"
//                                 value={skill.demand}
//                                 color="success"
//                                 sx={{
//                                   height: 6,
//                                   borderRadius: 3,
//                                   mb: 1,
//                                   backgroundColor: "divider",
//                                 }}
//                               />
//                               <Typography
//                                 variant="body2"
//                                 color="text.secondary"
//                               >
//                                 {skill.placements.toLocaleString()} Placement
//                               </Typography>
//                             </>
//                           )}
//                         </Paper>
//                       )
//                     )}
//                   </Stack>
//                 </Box>
//               </Paper>
//             </Box>
//             <Box sx={{ flex: { md: 2 }, minWidth: 0 }}>
//               <Paper
//                 elevation={0}
//                 sx={{
//                   ...sectionPaperStyles,
//                   height: "100%",
//                   display: "flex",
//                   flexDirection: "column",
//                 }}
//               >
//                 <Typography
//                   variant="h6"
//                   sx={{
//                     fontWeight: "bold",
//                     mb: 2,
//                     color: theme.palette.primary.main,
//                   }}
//                 >
//                   Top 5 Performing Institutions
//                 </Typography>
//                 <TableContainer>
//                   <Table size="small">
//                     <TableBody>
//                       {(loading
//                         ? [...Array(5)]
//                         : institutionData.slice(0, 5)
//                       ).map((inst, index) => (
//                         <TableRow
//                           key={index}
//                           sx={{ "& td, & th": { border: 0, py: 1.5 } }}
//                         >
//                           {loading ? (
//                             <TableCell colSpan={5}>
//                               <Skeleton />
//                             </TableCell>
//                           ) : (
//                             <>
//                               <TableCell
//                                 sx={{ width: "1%", fontWeight: "bold" }}
//                               >
//                                 {index + 1}.
//                               </TableCell>
//                               <TableCell sx={{ width: "49%" }}>
//                                 <Box display="flex" alignItems="center">
//                                   <School
//                                     sx={{
//                                       mr: 1.5,
//                                       fontSize: 18,
//                                       color: "text.secondary",
//                                     }}
//                                   />
//                                   <Typography variant="body2" fontWeight="bold">
//                                     {inst.institution}
//                                   </Typography>
//                                 </Box>
//                               </TableCell>
//                               <TableCell align="center">
//                                 {inst.graduates}{" "}
//                                 <Typography
//                                   variant="caption"
//                                   color="text.secondary"
//                                 >
//                                   Grads
//                                 </Typography>
//                               </TableCell>
//                               <TableCell align="center">
//                                 {inst.placements}{" "}
//                                 <Typography
//                                   variant="caption"
//                                   color="text.secondary"
//                                 >
//                                   Hired
//                                 </Typography>
//                               </TableCell>
//                               <TableCell align="right">
//                                 <Chip
//                                   label={`${inst.rate}%`}
//                                   size="small"
//                                   sx={{
//                                     backgroundColor:
//                                       inst.rate >= 90
//                                         ? alpha(
//                                             theme.palette.success.main,
//                                             0.15
//                                           )
//                                         : alpha(
//                                             theme.palette.warning.main,
//                                             0.15
//                                           ),
//                                     color:
//                                       inst.rate >= 90
//                                         ? theme.palette.success.dark
//                                         : theme.palette.warning.dark,
//                                     fontWeight: "bold",
//                                   }}
//                                 />
//                               </TableCell>
//                             </>
//                           )}
//                         </TableRow>
//                       ))}
//                     </TableBody>
//                   </Table>
//                 </TableContainer>
//                 <Box
//                   sx={{
//                     mt: "auto",
//                     pt: 1,
//                     display: "flex",
//                     justifyContent: "flex-end",
//                   }}
//                 >
//                   <Button size="small" onClick={handleOpenDialog}>
//                     See All
//                   </Button>
//                 </Box>
//               </Paper>
//             </Box>
//           </Box>

//           {/* ROW 4: REGIONAL PERFORMANCE */}
//           <Paper elevation={0} sx={sectionPaperStyles}>
//             <Typography
//               variant="h6"
//               sx={{
//                 fontWeight: "bold",
//                 mb: 2,
//                 color: theme.palette.primary.main,
//               }}
//             >
//               Regional Performance
//             </Typography>
//             <TableContainer>
//               <Table>
//                 <TableHead>
//                   <TableRow>
//                     <TableCell>Region</TableCell>
//                     <TableCell>Placement</TableCell>
//                     <TableCell>Growth</TableCell>
//                     <TableCell>Avg Salary</TableCell>
//                     <TableCell>Demand Score</TableCell>
//                     <TableCell>Top Roles</TableCell>
//                   </TableRow>
//                 </TableHead>
//                 <TableBody>
//                   {(loading
//                     ? [...Array(regionalRowsPerPage)]
//                     : regionData.slice(
//                         regionalPage * regionalRowsPerPage,
//                         regionalPage * regionalRowsPerPage + regionalRowsPerPage
//                       )
//                   ).map((item, index) => (
//                     <TableRow
//                       key={index}
//                       sx={{
//                         "&:hover": {
//                           backgroundColor: theme.palette.action.hover,
//                         },
//                       }}
//                     >
//                       {loading ? (
//                         <TableCell colSpan={6}>
//                           <Skeleton />
//                         </TableCell>
//                       ) : (
//                         <>
//                           <TableCell sx={{ fontWeight: "bold" }}>
//                             {item.region}
//                           </TableCell>
//                           <TableCell
//                             sx={{ color: "error.main", fontWeight: 500 }}
//                           >
//                             {item.placements.toLocaleString()}
//                           </TableCell>
//                           <TableCell
//                             sx={{ color: "success.main", fontWeight: 500 }}
//                           >
//                             +{item.growth}%
//                           </TableCell>
//                           <TableCell sx={{ fontWeight: "bold" }}>
//                             {item.avgSalary}
//                           </TableCell>
//                           <TableCell>
//                             <Box display="flex" alignItems="center">
//                               <LinearProgress
//                                 variant="determinate"
//                                 value={item.demand_score}
//                                 color="success"
//                                 sx={{
//                                   width: 60,
//                                   mr: 1,
//                                   backgroundColor: "divider",
//                                 }}
//                               />
//                               <Typography
//                                 variant="caption"
//                                 sx={{ color: "success.main", fontWeight: 500 }}
//                               >
//                                 {item.demand_score}%
//                               </Typography>
//                             </Box>
//                           </TableCell>
//                           <TableCell>
//                             <Typography variant="body2">
//                               {(item.top_roles || []).join(", ")}
//                             </Typography>
//                           </TableCell>
//                         </>
//                       )}
//                     </TableRow>
//                   ))}
//                 </TableBody>
//               </Table>
//             </TableContainer>
//             <TablePagination
//               component="div"
//               count={regionData.length}
//               page={regionalPage}
//               onPageChange={handleRegionalChangePage}
//               rowsPerPage={regionalRowsPerPage}
//               onRowsPerPageChange={handleRegionalChangeRowsPerPage}
//               rowsPerPageOptions={[5, 10, 25]}
//               sx={{ pt: 1 }}
//             />
//           </Paper>
//         </Stack>
//       </Box>

//       {/* DIALOG FOR ALL INSTITUTIONS */}
//       <Dialog
//         open={dialogOpen}
//         onClose={handleCloseDialog}
//         fullWidth
//         maxWidth="md"
//       >
//         <DialogTitle
//           sx={{
//             display: "flex",
//             justifyContent: "space-between",
//             alignItems: "center",
//           }}
//         >
//           All Performing Institutions
//           <IconButton onClick={handleCloseDialog}>
//             <CloseIcon />
//           </IconButton>
//         </DialogTitle>
//         <DialogContent dividers>
//           <TextField
//             fullWidth
//             variant="outlined"
//             size="small"
//             label="Search by Institution Name"
//             value={searchTerm}
//             onChange={(e) => setSearchTerm(e.target.value)}
//             sx={{ mb: 2 }}
//           />
//           <TableContainer component={Paper} elevation={0}>
//             <Table stickyHeader size="small">
//               <TableHead>
//                 <TableRow>
//                   <TableCell sx={{ width: "5%" }}>Sr.No.</TableCell>
//                   <TableCell>Institution</TableCell>
//                   <TableCell align="center">Graduates</TableCell>
//                   <TableCell align="center">Placements</TableCell>
//                   <TableCell align="right">Success Rate</TableCell>
//                 </TableRow>
//               </TableHead>
//               <TableBody>
//                 {filteredInstitutions.map((inst, index) => (
//                   <TableRow key={index}>
//                     <TableCell sx={{ fontWeight: "bold" }}>
//                       {index + 1}.
//                     </TableCell>
//                     <TableCell>
//                       <Box display="flex" alignItems="center">
//                         <School
//                           sx={{
//                             mr: 1.5,
//                             fontSize: 18,
//                             color: "text.secondary",
//                           }}
//                         />
//                         <Typography variant="body2">
//                           {inst.institution}
//                         </Typography>
//                       </Box>
//                     </TableCell>
//                     <TableCell align="center">{inst.graduates}</TableCell>
//                     <TableCell align="center">{inst.placements}</TableCell>
//                     <TableCell align="right">
//                       <Chip
//                         label={`${inst.rate}%`}
//                         size="small"
//                         sx={{
//                           backgroundColor:
//                             inst.rate >= 90
//                               ? alpha(theme.palette.success.main, 0.15)
//                               : alpha(theme.palette.warning.main, 0.15),
//                           color:
//                             inst.rate >= 90
//                               ? theme.palette.success.dark
//                               : theme.palette.warning.dark,
//                           fontWeight: "bold",
//                         }}
//                       />
//                     </TableCell>
//                   </TableRow>
//                 ))}
//               </TableBody>
//             </Table>
//           </TableContainer>
//         </DialogContent>
//         <DialogActions>
//           <Button onClick={handleCloseDialog}>Close</Button>
//         </DialogActions>
//       </Dialog>
//     </Layout>
//   );
// };

// export default Analytics;
// "use client";

// import { useState, useEffect, useMemo } from "react";
// import {
//   Grid,
//   Card,
//   CardContent,
//   Typography,
//   Box,
//   FormControl,
//   Select,
//   MenuItem,
//   Button,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   LinearProgress,
//   Chip,
//   Paper,
//   Skeleton,
//   Stack,
//   TablePagination,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   TextField,
//   IconButton,
// } from "@mui/material";
// import { useTheme, alpha } from "@mui/material/styles";
// import {
//   School,
//   AccessTime,
//   Public,
//   FileDownloadOutlined,
//   KeyboardArrowDown,
//   Close as CloseIcon,
// } from "@mui/icons-material";
// import Layout from "../common/Layout";
// import { useNotification } from "../../context/NotificationContext";
// import apiClient from "../../context/apiClient";

// const Analytics = () => {
//   const theme = useTheme();
//   const { showNotification } = useNotification();
//   const [timeRange, setTimeRange] = useState("month");
//   const [region, setRegion] = useState("all");
//   const [loading, setLoading] = useState(true);

//   // --- STATE FOR DIALOG & SEARCH ---
//   const [dialogOpen, setDialogOpen] = useState(false);
//   const [searchTerm, setSearchTerm] = useState("");

//   // --- STATE FOR REGIONAL PERFORMANCE PAGINATION ---
//   const [regionalPage, setRegionalPage] = useState(0);
//   const [regionalRowsPerPage, setRegionalRowsPerPage] = useState(5);

//   const [globalMetrics, setGlobalMetrics] = useState({
//     totalTalent: 0,
//     activePlacements: 0,
//     totalEmployers: 0,
//     successRate: 0,
//     avgPlacementTime: 0,
//   });
//   const [regionData, setRegionData] = useState([]);
//   const [skillDemand, setSkillDemand] = useState([]);
//   const [institutionData, setInstitutionData] = useState([]);

//   // --- HANDLERS FOR REGIONAL PAGINATION ---
//   const handleRegionalChangePage = (event, newPage) => {
//     setRegionalPage(newPage);
//   };
//   const handleRegionalChangeRowsPerPage = (event) => {
//     setRegionalRowsPerPage(parseInt(event.target.value, 10));
//     setRegionalPage(0);
//   };

//   // --- HANDLERS FOR DIALOG ---
//   const handleOpenDialog = () => setDialogOpen(true);
//   const handleCloseDialog = () => setDialogOpen(false);

//   // Memoize the filtered list for the dialog search
//   const filteredInstitutions = useMemo(() => {
//     if (!searchTerm) {
//       return institutionData;
//     }
//     return institutionData.filter((inst) =>
//       inst.institution.toLowerCase().includes(searchTerm.toLowerCase())
//     );
//   }, [searchTerm, institutionData]);

//   useEffect(() => {
//     const fetchDashboardData = async () => {
//       setLoading(true);
//       try {
//         const response = await apiClient.get("/dashboard/global-overview/");
//         const data = response.data;
//         setGlobalMetrics({
//           totalTalent: data.global_ai_talent || 0,
//           activePlacements: data.active_placements || 0,
//           totalEmployers: data.global_employer || 0,
//           successRate: Number.parseFloat(data.success_rate || 0).toFixed(2),
//           avgPlacementTime: Number.parseFloat(
//             data.average_days_to_place || 0
//           ).toFixed(0),
//         });
//         const topRolesExamples = [
//           ["AI Engineer"],
//           ["Java Developer"],
//           ["Python", "HTML"],
//         ];
//         setRegionData(
//           (data.placements_by_region || []).map((item, index) => ({
//             ...item,
//             avgSalary: `$${Number(item.avg_salary || 0).toLocaleString()}`,
//             top_roles:
//               item.top_roles ||
//               topRolesExamples[index % topRolesExamples.length],
//           }))
//         );
//         const skills = data.skills_in_high_demand || [];
//         const maxPlacements = Math.max(...skills.map((s) => s.placements), 0);
//         setSkillDemand(
//           skills.map((item) => ({
//             ...item,
//             demand:
//               maxPlacements > 0 ? (item.placements / maxPlacements) * 100 : 0,
//           }))
//         );
//         setInstitutionData(
//           (data.top_performing_institutions || []).map((item) => ({
//             institution: item.institution_name || "Unknown Institution",
//             graduates: item.graduates || 0,
//             placements: item.placements || 0,
//             rate: Number.parseFloat(item.success_rate || 0).toFixed(0),
//           }))
//         );
//       } catch (error) {
//         console.error("Failed to fetch dashboard data:", error);
//         showNotification(
//           "Failed to load dashboard analytics. Please try again later.",
//           "error"
//         );
//       } finally {
//         setLoading(false);
//       }
//     };
//     fetchDashboardData();
//   }, [showNotification]);

//   const handleExportReport = () => {
//     showNotification("Analytics report exported successfully!", "success");
//   };

//   const kpiCards = [
//     { title: "TOTAL AI TALENT", value: globalMetrics.totalTalent },
//     { title: "ACTIVE PLACEMENT", value: globalMetrics.activePlacements },
//     { title: "GLOBAL EMPLOYERS", value: globalMetrics.totalEmployers },
//     { title: "SUCCESS RATE", value: `${globalMetrics.successRate}%` },
//     { title: "AVG DAYS TO PLACE", value: globalMetrics.avgPlacementTime },
//   ];

//   const sectionPaperStyles = {
//     p: 3,
//     borderRadius: "20px",
//     boxShadow: theme.shadows[1],
//     backgroundColor: theme.palette.mint.main,
//   };

//   return (
//     <Layout title="Analytics Dashboard">
//       <Box
//         sx={{
//           p: { xs: 2, md: 3 },
//           backgroundColor: theme.palette.background.default,
//         }}
//       >
//         <Stack direction="column" spacing={3}>
//           {/* ROW 1: HEADER PANEL */}
//           <Paper
//             elevation={0}
//             sx={{
//               ...sectionPaperStyles,
//               display: "flex",
//               justifyContent: "space-between",
//               alignItems: { xs: "flex-start", md: "center" },
//               flexDirection: { xs: "column", md: "row" },
//             }}
//           >
//             <Box mb={{ xs: 3, md: 0 }}>
//               <Typography
//                 variant="h4"
//                 sx={{ fontWeight: "bold", color: theme.palette.primary.main }}
//               >
//                 Analytics
//               </Typography>
//               <Typography variant="body1" color="text.secondary">
//                 Comprehensive insight into AI Talent export Performance
//               </Typography>
//             </Box>
//             <Box
//               display="flex"
//               gap={2}
//               flexWrap="wrap"
//               justifyContent={{ xs: "flex-start", md: "flex-end" }}
//             >
//               <FormControl sx={{ minWidth: 160 }}>
//                 <Select
//                   value={timeRange}
//                   onChange={(e) => setTimeRange(e.target.value)}
//                   IconComponent={KeyboardArrowDown}
//                   size="small"
//                   sx={{
//                     borderRadius: "50px",
//                     color: "primary.main",
//                     "& .MuiOutlinedInput-notchedOutline": {
//                       borderColor: "primary.main",
//                     },
//                     "& .MuiSvgIcon-root": { color: "primary.main" },
//                   }}
//                   renderValue={(value) => (
//                     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                       <AccessTime fontSize="small" />
//                       <Typography variant="body2">
//                         {value === "week"
//                           ? "Last Week"
//                           : value === "month"
//                           ? "Last Month"
//                           : value === "quarter"
//                           ? "Last Quarter"
//                           : "Last Year"}
//                       </Typography>
//                     </Box>
//                   )}
//                 >
//                   <MenuItem value="week">Last Week</MenuItem>
//                   <MenuItem value="month">Last Month</MenuItem>
//                   <MenuItem value="quarter">Last Quarter</MenuItem>
//                   <MenuItem value="year">Last Year</MenuItem>
//                 </Select>
//               </FormControl>
//               <FormControl sx={{ minWidth: 160 }}>
//                 <Select
//                   value={region}
//                   onChange={(e) => setRegion(e.target.value)}
//                   IconComponent={KeyboardArrowDown}
//                   size="small"
//                   sx={{
//                     borderRadius: "50px",
//                     color: "primary.main",
//                     "& .MuiOutlinedInput-notchedOutline": {
//                       borderColor: "primary.main",
//                     },
//                     "& .MuiSvgIcon-root": { color: "primary.main" },
//                   }}
//                   renderValue={(value) => (
//                     <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//                       <Public fontSize="small" />
//                       <Typography variant="body2">
//                         {value === "all" ? "All Regions" : value}
//                       </Typography>
//                     </Box>
//                   )}
//                 >
//                   <MenuItem value="all">All Regions</MenuItem>
//                   <MenuItem value="UAE">UAE</MenuItem>
//                   <MenuItem value="USA">USA</MenuItem>
//                   <MenuItem value="EU">EU</MenuItem>
//                   <MenuItem value="Singapore">Singapore</MenuItem>
//                 </Select>
//               </FormControl>
//               <Button
//                 variant="outlined"
//                 color="primary"
//                 startIcon={<FileDownloadOutlined />}
//                 onClick={handleExportReport}
//                 sx={{
//                   borderRadius: "50px",
//                   textTransform: "none",
//                   height: "40px",
//                 }}
//               >
//                 Export Report
//               </Button>
//             </Box>
//           </Paper>

//           {/* ROW 2: KPI CARDS */}
//           <Box
//             sx={{
//               overflowX: { xs: "auto", md: "visible" },
//               pb: { xs: 1, md: 0 },
//             }}
//           >
//             <Box display="flex" gap={{ xs: 1.5, md: 2 }}>
//               {(loading ? [...Array(5)] : kpiCards).map((item, index) => (
//                 <Box
//                   key={index}
//                   sx={{ flex: { md: 1 }, minWidth: { xs: 180, md: 0 } }}
//                 >
//                   {loading ? (
//                     <Skeleton
//                       variant="rectangular"
//                       width="100%"
//                       height={120}
//                       sx={{ borderRadius: "14px" }}
//                     />
//                   ) : (
//                     <Card
//                       sx={{
//                         position: "relative",
//                         backgroundColor: theme.palette.primary.main,
//                         color: theme.palette.primary.contrastText,
//                         height: "120px",
//                         borderRadius: "14px",
//                         overflow: "hidden",
//                         boxShadow: theme.shadows[2],
//                       }}
//                     >
//                       <svg
//                         width="80"
//                         height="120"
//                         viewBox="0 0 80 120"
//                         style={{ position: "absolute", right: 0, top: 0 }}
//                       >
//                         <path
//                           d="M20 0 Q60 30 40 60 Q20 90 60 120 L80 120 L80 0 Z"
//                           fill={theme.palette.accent.main}
//                         />
//                       </svg>
//                       <CardContent
//                         sx={{
//                           position: "relative",
//                           zIndex: 1,
//                           p: { xs: 1.5, md: 2 },
//                         }}
//                       >
//                         <Typography
//                           sx={{
//                             fontWeight: "bold",
//                             fontSize: { xs: "0.7rem", md: "0.8rem" },
//                             textTransform: "uppercase",
//                             color: alpha(
//                               theme.palette.primary.contrastText,
//                               0.8
//                             ),
//                             mb: 1,
//                             lineHeight: 1.2,
//                           }}
//                         >
//                           {item.title}
//                         </Typography>
//                         <Typography
//                           sx={{
//                             fontWeight: "bold",
//                             fontSize: { xs: "1.5rem", md: "1.75rem" },
//                             lineHeight: 1,
//                           }}
//                         >
//                           {item.value}
//                         </Typography>
//                       </CardContent>
//                     </Card>
//                   )}
//                 </Box>
//               ))}
//             </Box>
//           </Box>

//           {/* ROW 3: TWO COLUMNS (SKILLS & INSTITUTIONS) */}
//           <Box
//             sx={{
//               display: "flex",
//               flexDirection: { xs: "column", md: "row" },
//               gap: 3,
//             }}
//           >
//             <Box sx={{ flex: { md: 1 }, minWidth: 0 }}>
//               <Paper
//                 elevation={0}
//                 sx={{
//                   ...sectionPaperStyles,
//                   height: "100%",
//                   display: "flex",
//                   flexDirection: "column",
//                 }}
//               >
//                 <Typography
//                   variant="h6"
//                   sx={{
//                     fontWeight: "bold",
//                     mb: 2,
//                     color: theme.palette.primary.main,
//                   }}
//                 >
//                   Skill In High Demand
//                 </Typography>
//                 <Box sx={{ maxHeight: 450, overflowY: "auto", pr: 1 }}>
//                   <Stack spacing={1.5}>
//                     {(loading ? [...Array(5)] : skillDemand).map(
//                       (skill, index) => (
//                         <Paper
//                           key={index}
//                           elevation={0}
//                           sx={{
//                             p: 2,
//                             borderRadius: "12px",
//                             border: `1px solid ${theme.palette.divider}`,
//                             backgroundColor: "background.paper",
//                           }}
//                         >
//                           {loading ? (
//                             <Skeleton height={60} />
//                           ) : (
//                             <>
//                               <Box
//                                 display="flex"
//                                 justifyContent="space-between"
//                                 alignItems="center"
//                                 mb={1}
//                               >
//                                 <Typography
//                                   variant="body1"
//                                   sx={{ fontWeight: "bold" }}
//                                 >
//                                   {skill.skill}
//                                 </Typography>
//                                 <Chip
//                                   label={`+${skill.growth}%`}
//                                   size="small"
//                                   sx={{
//                                     backgroundColor: alpha(
//                                       theme.palette.warning.main,
//                                       0.2
//                                     ),
//                                     color:
//                                       theme.palette.warning.dark ||
//                                       theme.palette.warning.main,
//                                     fontWeight: "bold",
//                                   }}
//                                 />
//                               </Box>
//                               <LinearProgress
//                                 variant="determinate"
//                                 value={skill.demand}
//                                 color="success"
//                                 sx={{
//                                   height: 6,
//                                   borderRadius: 3,
//                                   mb: 1,
//                                   backgroundColor: "divider",
//                                 }}
//                               />
//                               <Typography
//                                 variant="body2"
//                                 color="text.secondary"
//                               >
//                                 {skill.placements.toLocaleString()} Placement
//                               </Typography>
//                             </>
//                           )}
//                         </Paper>
//                       )
//                     )}
//                   </Stack>
//                 </Box>
//               </Paper>
//             </Box>
//             <Box sx={{ flex: { md: 2 }, minWidth: 0 }}>
//               <Paper
//                 elevation={0}
//                 sx={{
//                   ...sectionPaperStyles,
//                   height: "100%",
//                   display: "flex",
//                   flexDirection: "column",
//                 }}
//               >
//                 <Typography
//                   variant="h6"
//                   sx={{
//                     fontWeight: "bold",
//                     mb: 2,
//                     color: theme.palette.primary.main,
//                   }}
//                 >
//                   Top 5 Performing Institutions
//                 </Typography>
//                 <TableContainer>
//                   <Table size="small">
//                     <TableBody>
//                       {(loading
//                         ? [...Array(5)]
//                         : institutionData.slice(0, 5)
//                       ).map((inst, index) => (
//                         <TableRow
//                           key={index}
//                           sx={{ "& td, & th": { border: 0, py: 1.5 } }}
//                         >
//                           {loading ? (
//                             <TableCell colSpan={5}>
//                               <Skeleton />
//                             </TableCell>
//                           ) : (
//                             <>
//                               <TableCell
//                                 sx={{ width: "1%", fontWeight: "bold" }}
//                               >
//                                 {index + 1}.
//                               </TableCell>
//                               <TableCell sx={{ width: "49%" }}>
//                                 <Box display="flex" alignItems="center">
//                                   <School
//                                     sx={{
//                                       mr: 1.5,
//                                       fontSize: 18,
//                                       color: "text.secondary",
//                                     }}
//                                   />
//                                   <Typography variant="body2" fontWeight="bold">
//                                     {inst.institution}
//                                   </Typography>
//                                 </Box>
//                               </TableCell>
//                               <TableCell align="center">
//                                 {inst.graduates}{" "}
//                                 <Typography
//                                   variant="caption"
//                                   color="text.secondary"
//                                 >
//                                   Grads
//                                 </Typography>
//                               </TableCell>
//                               <TableCell align="center">
//                                 {inst.placements}{" "}
//                                 <Typography
//                                   variant="caption"
//                                   color="text.secondary"
//                                 >
//                                   Hired
//                                 </Typography>
//                               </TableCell>
//                               <TableCell align="right">
//                                 <Chip
//                                   label={`${inst.rate}%`}
//                                   size="small"
//                                   sx={{
//                                     backgroundColor:
//                                       inst.rate >= 90
//                                         ? alpha(
//                                             theme.palette.success.main,
//                                             0.15
//                                           )
//                                         : alpha(
//                                             theme.palette.warning.main,
//                                             0.15
//                                           ),
//                                     color:
//                                       inst.rate >= 90
//                                         ? theme.palette.success.dark
//                                         : theme.palette.warning.dark,
//                                     fontWeight: "bold",
//                                   }}
//                                 />
//                               </TableCell>
//                             </>
//                           )}
//                         </TableRow>
//                       ))}
//                     </TableBody>
//                   </Table>
//                 </TableContainer>
//                 <Box
//                   sx={{
//                     mt: "auto",
//                     pt: 1,
//                     display: "flex",
//                     justifyContent: "flex-end",
//                   }}
//                 >
//                   <Button size="small" onClick={handleOpenDialog}>
//                     See All
//                   </Button>
//                 </Box>
//               </Paper>
//             </Box>
//           </Box>

//           {/* ROW 4: REGIONAL PERFORMANCE */}
//           <Paper elevation={0} sx={sectionPaperStyles}>
//             <Typography
//               variant="h6"
//               sx={{
//                 fontWeight: "bold",
//                 mb: 2,
//                 color: theme.palette.primary.main,
//               }}
//             >
//               Regional Performance
//             </Typography>
//             <TableContainer>
//               <Table>
//                 <TableHead>
//                   <TableRow>
//                     <TableCell>Region</TableCell>
//                     <TableCell>Placement</TableCell>
//                     <TableCell>Growth</TableCell>
//                     <TableCell>Avg Salary</TableCell>
//                     <TableCell>Demand Score</TableCell>
//                     <TableCell>Top Roles</TableCell>
//                   </TableRow>
//                 </TableHead>
//                 <TableBody>
//                   {(loading
//                     ? [...Array(regionalRowsPerPage)]
//                     : regionData.slice(
//                         regionalPage * regionalRowsPerPage,
//                         regionalPage * regionalRowsPerPage + regionalRowsPerPage
//                       )
//                   ).map((item, index) => (
//                     <TableRow
//                       key={index}
//                       sx={{
//                         "&:hover": {
//                           backgroundColor: theme.palette.action.hover,
//                         },
//                       }}
//                     >
//                       {loading ? (
//                         <TableCell colSpan={6}>
//                           <Skeleton />
//                         </TableCell>
//                       ) : (
//                         <>
//                           <TableCell sx={{ fontWeight: "bold" }}>
//                             {item.region}
//                           </TableCell>
//                           <TableCell
//                             sx={{ color: "error.main", fontWeight: 500 }}
//                           >
//                             {item.placements.toLocaleString()}
//                           </TableCell>
//                           <TableCell
//                             sx={{ color: "success.main", fontWeight: 500 }}
//                           >
//                             +{item.growth}%
//                           </TableCell>
//                           <TableCell sx={{ fontWeight: "bold" }}>
//                             {item.avgSalary}
//                           </TableCell>
//                           <TableCell>
//                             <Box display="flex" alignItems="center">
//                               <LinearProgress
//                                 variant="determinate"
//                                 value={item.demand_score}
//                                 color="success"
//                                 sx={{
//                                   width: 60,
//                                   mr: 1,
//                                   backgroundColor: "divider",
//                                 }}
//                               />
//                               <Typography
//                                 variant="caption"
//                                 sx={{ color: "success.main", fontWeight: 500 }}
//                               >
//                                 {item.demand_score}%
//                               </Typography>
//                             </Box>
//                           </TableCell>
//                           <TableCell>
//                             <Typography variant="body2">
//                               {(item.top_roles || []).join(", ")}
//                             </Typography>
//                           </TableCell>
//                         </>
//                       )}
//                     </TableRow>
//                   ))}
//                 </TableBody>
//               </Table>
//             </TableContainer>
//             <TablePagination
//               component="div"
//               count={regionData.length}
//               page={regionalPage}
//               onPageChange={handleRegionalChangePage}
//               rowsPerPage={regionalRowsPerPage}
//               onRowsPerPageChange={handleRegionalChangeRowsPerPage}
//               rowsPerPageOptions={[5, 10, 25]}
//               sx={{ pt: 1 }}
//             />
//           </Paper>
//         </Stack>
//       </Box>

//       {/* DIALOG FOR ALL INSTITUTIONS */}
//       <Dialog
//         open={dialogOpen}
//         onClose={handleCloseDialog}
//         fullWidth
//         maxWidth="md"
//       >
//         <DialogTitle
//           sx={{
//             display: "flex",
//             justifyContent: "space-between",
//             alignItems: "center",
//           }}
//         >
//           All Performing Institutions
//           <IconButton onClick={handleCloseDialog}>
//             <CloseIcon />
//           </IconButton>
//         </DialogTitle>
//         <DialogContent dividers>
//           <TextField
//             fullWidth
//             variant="outlined"
//             size="small"
//             label="Search by Institution Name"
//             value={searchTerm}
//             onChange={(e) => setSearchTerm(e.target.value)}
//             sx={{ mb: 2 }}
//           />
//           <TableContainer component={Paper} elevation={0}>
//             <Table stickyHeader size="small">
//               <TableHead>
//                 <TableRow>
//                   <TableCell sx={{ width: "5%" }}>Sr.No.</TableCell>
//                   <TableCell>Institution</TableCell>
//                   <TableCell align="center">Graduates</TableCell>
//                   <TableCell align="center">Placements</TableCell>
//                   <TableCell align="right">Success Rate</TableCell>
//                 </TableRow>
//               </TableHead>
//               <TableBody>
//                 {filteredInstitutions.map((inst, index) => (
//                   <TableRow key={index}>
//                     <TableCell sx={{ fontWeight: "bold" }}>
//                       {index + 1}.
//                     </TableCell>
//                     <TableCell>
//                       <Box display="flex" alignItems="center">
//                         <School
//                           sx={{
//                             mr: 1.5,
//                             fontSize: 18,
//                             color: "text.secondary",
//                           }}
//                         />
//                         <Typography variant="body2">
//                           {inst.institution}
//                         </Typography>
//                       </Box>
//                     </TableCell>
//                     <TableCell align="center">{inst.graduates}</TableCell>
//                     <TableCell align="center">{inst.placements}</TableCell>
//                     <TableCell align="right">
//                       <Chip
//                         label={`${inst.rate}%`}
//                         size="small"
//                         sx={{
//                           backgroundColor:
//                             inst.rate >= 90
//                               ? alpha(theme.palette.success.main, 0.15)
//                               : alpha(theme.palette.warning.main, 0.15),
//                           color:
//                             inst.rate >= 90
//                               ? theme.palette.success.dark
//                               : theme.palette.warning.dark,
//                           fontWeight: "bold",
//                         }}
//                       />
//                     </TableCell>
//                   </TableRow>
//                 ))}
//               </TableBody>
//             </Table>
//           </TableContainer>
//         </DialogContent>
//         <DialogActions>
//           <Button onClick={handleCloseDialog}>Close</Button>
//         </DialogActions>
//       </Dialog>
//     </Layout>
//   );
// };

// export default Analytics;
